import type { User } from '@supabase/supabase-js';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  ChevronDown,
  CircleDollarSign,
  CircleHelp,
  Cloud,
  CloudOff,
  Copy,
  CreditCard,
  FileCheck2,
  FileClock,
  FileOutput,
  FilePlus2,
  FileText,
  FileSignature,
  HardDriveDownload,
  LayoutDashboard,
  LogOut,
  Menu,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  QrCode,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
const SystemStatusDashboard=lazy(()=>import('./components/SystemStatusDashboard').then(module=>({default:module.SystemStatusDashboard})));
const CloudExpiryBanner=lazy(()=>import('./components/CloudExpiryBanner').then(module=>({default:module.CloudExpiryBanner})));
import type {CloudExpiryState} from '../shared/cloudExpiry';
import { QuoteEditor } from './components/QuoteEditor';
import { QuotePreview } from './components/QuotePreview';
import { InvoiceManager } from './components/InvoiceManager';
import { QuoteAcceptanceModal } from './components/QuoteAcceptanceModal';
import { ProReceivablesView } from './components/ProReceivablesView';
import { AuthScreen } from './components/AuthScreen';
import { AccountSecurity } from './components/AccountSecurity';
import { CloudStorageCard } from './components/CloudStorageCard';
import { StorageRenewalManager } from './components/StorageRenewalManager';
import { ManualStoragePanel } from './components/ManualStoragePanel';
import { BillingCheckoutRecovery } from './components/BillingCheckoutRecovery';
import { fetchBillingRights } from './lib/billingRights';
import { DocumentTrashView } from './components/DocumentTrashView';
import { collectTrash,type TrashItem } from './lib/documentTrash';
import { cloudTrashAction } from './lib/r2Sync';
import { OnboardingScreen } from './components/OnboardingScreen';
import { PublicLanding } from './components/PublicLanding';
import { UsageAnalyticsChoiceScreen } from './components/UsageAnalyticsChoiceScreen';
import { deleteLocalDataForOwner, getLegacyDataSummary, getSettings, listInvoices, listPayments, listQuotes, markInvoiceDeleted, markQuoteDeleted, migrateLegacyDataToActive, purgeExpiredLocalOnlyTrash,replaceLocalData, reserveDocumentNumber,restoreLocalDocument, saveInvoice, savePayment, saveQuote, saveSettings, setLocalDataOwner } from './lib/db';
import { createInvoiceRecord, createPaymentRecord, createRevision } from './lib/documentFlow';
import {thaiDate,thaiDateAfter} from '../shared/thaiDate';
import { backupSizeBytes, backupUsageLevel, MAX_BACKUP_BYTES, parseBackup } from './lib/backup';
import { isMissingCloudFile, missingCloudFileKeys } from './lib/cloudFileState';
import { useCloudFile } from './lib/useCloudFile';
import { hydrateBackupFiles } from './lib/remoteFiles';
import { withTimeout } from './lib/async';
import { isSupabaseConfigured, supabase } from './lib/supabase';
import { errorMessage, friendlyAppError, friendlySyncError } from './lib/errors';
import { monitoredFetch, reportClientError } from './lib/monitoring';
import { cancelDocumentSyncs, CloudSyncError, syncAllDocuments, syncStageLabel, type SyncStage } from './lib/sync';
import {CloudSyncNotifications} from './components/CloudSyncNotifications';
import { captureLocalDataScope } from './lib/db';
import { withDatabaseRetry } from './lib/supabaseRetry';
import { buildReceivablesReport } from './lib/receivables';
import { createDefaultSettings, invoiceBalance, invoicePaidAmount, invoiceStatus, quoteTotal, type AccountProfile, type AppSettings, type CustomerProfile, type Invoice, type InvoiceStatus, type Payment, type Plan, type Quote, type QuoteAcceptance, type QuoteStatus } from './types';

type View = 'dashboard' | 'quotes' | 'invoices' | 'reports' | 'trash' | 'settings' | 'help' | 'plans' | 'admin';

const statusMeta: Record<QuoteStatus, { label: string; className: string }> = {
  draft: { label: 'แบบร่าง', className: 'draft' },
  sent: { label: 'ส่งแล้ว', className: 'sent' },
  accepted: { label: 'ลูกค้ายอมรับแล้ว', className: 'accepted' },
  rejected: { label: 'ลูกค้าปฏิเสธ', className: 'rejected' },
  expired: { label: 'หมดอายุ', className: 'expired' },
  replaced: { label: 'มีฉบับแก้ไขแล้ว', className: 'replaced' },
};

const invoiceStatusMeta: Record<InvoiceStatus, { label: string; className: string }> = {
  unpaid: { label: 'ยังไม่ชำระ', className: 'unpaid' },
  partial: { label: 'ชำระบางส่วน', className: 'partial' },
  paid: { label: 'ชำระครบ', className: 'paid' },
};

const money = (value: number) => `฿${value.toLocaleString('th-TH', { maximumFractionDigits: 0 })}`;
const formatStorage = (bytes: number) => {
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && value >= 1024; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value.toLocaleString('th-TH', { maximumFractionDigits: value >= 100 ? 0 : value >= 10 ? 1 : 2 })} ${unit}`;
};
const shortDate = (value: string) => new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }).format(new Date(value));
const dateTime = (value: string) => new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
const isPlaceholderAccountName = (value: string | null | undefined) => !value?.trim() || value.trim() === 'บริษัทของคุณ';
const authAccountName = (authUser: User) => {
  const metadata = authUser.user_metadata as Record<string, unknown> | undefined;
  const name = [metadata?.full_name, metadata?.name, metadata?.display_name]
    .find((value): value is string => typeof value === 'string' && !isPlaceholderAccountName(value));
  return name?.trim() || '';
};
const resolveAccountName = (...values: Array<string | null | undefined>) => values
  .find((value): value is string => !isPlaceholderAccountName(value))?.trim() || '';
const readApiJson = async <T,>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new Error('Admin API ยังไม่ทำงาน กรุณาเปิด BilaFlow ผ่าน Cloudflare Pages runtime');
  }
  return response.json() as Promise<T>;
};

function App() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [trashRecords,setTrashRecords]=useState<TrashItem[]>([]);
  const [trashWorking,setTrashWorking]=useState(false);
  const [settings, setSettings] = useState<AppSettings>(createDefaultSettings);
  const [view, setView] = useState<View>(() => new URLSearchParams(window.location.search).get('account') === 'security' ? 'settings' : 'dashboard');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'all'>('all');
  const [editorQuote, setEditorQuote] = useState<Quote | null | undefined>(undefined);
  const [editorMode, setEditorMode] = useState<'new' | 'edit' | 'copy' | 'revision'>('new');
  const [previewQuote, setPreviewQuote] = useState<Quote | null>(null);
  const [managerInvoice, setManagerInvoice] = useState<Invoice | null>(null);
  const [acceptanceTarget, setAcceptanceTarget] = useState<{ quote: Quote; action: 'status' | 'invoice' | 'view' } | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [cloudExpiry,setCloudExpiry]=useState<CloudExpiryState|null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(new URLSearchParams(window.location.search).get('recovery') === '1');
  const [publicRoute, setPublicRoute] = useState<'landing' | 'auth'>(window.location.hash === '#login' ? 'auth' : 'landing');
  const [authIntent, setAuthIntent] = useState<'signin' | 'signup'>('signin');
  const [initializationError, setInitializationError] = useState('');
  const [plan, setPlan] = useState<Plan>('free');
  const [isAdmin, setIsAdmin] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [syncError, setSyncError] = useState('');
  const syncBusyRef = useRef(false);
  const syncAgainRef=useRef<()=>void>(()=>{});
  const activeUserRef = useRef<string | null>(null);
  const sessionEpochRef=useRef(0);
  const [notice, setNotice] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const usageReportKeyRef = useRef('');
  const cloudActive = plan !== 'free' && settings.cloudSyncEnabled;

  useEffect(() => {
    if (!accountMenuOpen) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!authReady || !user || new URLSearchParams(window.location.search).get('account') !== 'security') return;
    setView('settings');
    const url = new URL(window.location.href);
    url.searchParams.delete('account');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [authReady, user]);

  const refresh = async () => {
    const assertScope=captureLocalDataScope();
    await purgeExpiredLocalOnlyTrash();
    assertScope();
    const [storedQuotes, storedInvoices, storedPayments, storedSettings] = await Promise.all([listQuotes(true), listInvoices(true), listPayments(), getSettings()]);
    assertScope();
    const year = new Date().getFullYear();
    const prefix = `QT-${year}-`;
    const invoicePrefix = `INV-${year}-`;
    const receiptPrefix = `RE-${year}-`;
    const storedMax = storedQuotes.reduce((current, quote) => quote.number.startsWith(prefix) ? Math.max(current, Number(quote.number.slice(prefix.length)) || 0) : current, 0);
    const storedInvoiceMax = storedInvoices.reduce((current, invoice) => invoice.number.startsWith(invoicePrefix) ? Math.max(current, Number(invoice.number.slice(invoicePrefix.length)) || 0) : current, 0);
    const storedReceiptMax = storedPayments.reduce((current, payment) => payment.receiptNumber.startsWith(receiptPrefix) ? Math.max(current, Number(payment.receiptNumber.slice(receiptPrefix.length)) || 0) : current, 0);
    const normalizedSettings = {
      ...storedSettings,
      quoteSequenceYear: year,
      lastQuoteSequence: Math.max(storedSettings.quoteSequenceYear === year ? storedSettings.lastQuoteSequence : 0, storedMax),
      invoiceSequenceYear: year,
      lastInvoiceSequence: Math.max(storedSettings.invoiceSequenceYear === year ? storedSettings.lastInvoiceSequence : 0, storedInvoiceMax),
      receiptSequenceYear: year,
      lastReceiptSequence: Math.max(storedSettings.receiptSequenceYear === year ? storedSettings.lastReceiptSequence : 0, storedReceiptMax),
    };
    if (JSON.stringify(normalizedSettings) !== JSON.stringify(storedSettings)) await saveSettings(normalizedSettings);
    assertScope();
    setQuotes(storedQuotes.filter((quote) => !quote.deletedAt));
    setTrashRecords(collectTrash(storedQuotes,storedInvoices));
    const liveInvoices = storedInvoices.filter((invoice) => !invoice.deletedAt).map((invoice) => ({ ...invoice, status: invoiceStatus(invoice, storedPayments) }));
    setInvoices(liveInvoices);
    setPayments(storedPayments);
    setManagerInvoice((current) => current ? liveInvoices.find((invoice) => invoice.id === current.id) ?? null : null);
    setSettings(normalizedSettings);
  };

  useEffect(() => {
    if (!supabase) { setAuthReady(true); return; }
    const authClient = supabase;
    let active = true;
    let preparingUserId = '';

    const prepareSession = async (nextUser: User | null) => {
      if (!active) return;
      if (!nextUser) {
        sessionEpochRef.current++;activeUserRef.current=null;cancelDocumentSyncs();
        preparingUserId = '';
        setLocalDataOwner(null);
        setUser(null);
        setProfile(null);
        setCloudExpiry(null);
        setPlan('free');
        setIsAdmin(false);
        setQuotes([]);
        setInvoices([]);
        setPayments([]);
        setTrashRecords([]);
        setSettings(createDefaultSettings());setPreviewQuote(null);setManagerInvoice(null);setAcceptanceTarget(null);setEditorQuote(undefined);
        setSyncError('');setNotice('');setTrashWorking(false);
        setSearch('');setStatusFilter('all');setView('dashboard');setAccountMenuOpen(false);setMenuOpen(false);
        syncBusyRef.current=false; setSyncing(false); setSyncStatus('');
        setAuthReady(true);
        return;
      }
      if (preparingUserId === nextUser.id) {
        setUser(nextUser);
        return;
      }
      preparingUserId = nextUser.id;
      const epoch=++sessionEpochRef.current;
      const isCurrent=()=>active&&preparingUserId===nextUser.id&&sessionEpochRef.current===epoch;
      activeUserRef.current=nextUser.id;cancelDocumentSyncs();
      setQuotes([]);setInvoices([]);setPayments([]);setTrashRecords([]);setSettings(createDefaultSettings());
      setPreviewQuote(null);setManagerInvoice(null);setAcceptanceTarget(null);setEditorQuote(undefined);
      setProfile(null);setIsAdmin(false);setPlan('free');setSyncError('');setNotice('');
      setCloudExpiry(null);
      syncBusyRef.current=false;setSyncing(false);setSyncStatus('');setTrashWorking(false);
      setSearch('');setStatusFilter('all');setView('dashboard');setAccountMenuOpen(false);setMenuOpen(false);
      setAuthReady(false);
      setInitializationError('');
      setUser(nextUser);
      setLocalDataOwner(nextUser.id);
      try {
        const migrationKey = `quoteflow:legacy-migration:${nextUser.id}`;
        if (!localStorage.getItem(migrationKey)) {
          const legacy = await getLegacyDataSummary();
          if(!isCurrent())return;
          if (legacy.hasData) {
            const shouldMigrate = window.confirm(`พบข้อมูล BilaFlow เดิมในอุปกรณ์นี้ (${legacy.quotes} ใบเสนอราคา, ${legacy.invoices} ใบแจ้งหนี้) ต้องการนำมาใช้กับบัญชี ${nextUser.email ?? ''} หรือไม่? ข้อมูลต้นฉบับจะยังไม่ถูกลบ`);
            if (shouldMigrate) await migrateLegacyDataToActive();
          }
          localStorage.setItem(migrationKey, 'reviewed');
        }

        const { data, error } = await withTimeout(authClient.from('profiles')
          .select('user_id,display_name,plan,system_role,account_status,cloud_sync_enabled,onboarding_completed,terms_accepted_at,plan_expires_at')
          .eq('user_id', nextUser.id)
          .single());
        if(!isCurrent())return;
        if (error) throw new Error('ยังไม่ได้ติดตั้ง migration ระบบสมาชิก กรุณารันไฟล์ 202610210003_auth_accounts.sql ใน Supabase');
        if (data.account_status === 'suspended') {
          setProfile({ userId: data.user_id, displayName: data.display_name ?? '', plan: data.plan, systemRole: data.system_role, accountStatus: data.account_status, cloudSyncEnabled: data.cloud_sync_enabled, onboardingCompleted: data.onboarding_completed, termsAcceptedAt: data.terms_accepted_at, planExpiresAt: data.plan_expires_at });
          setPlan(data.plan);
          setIsAdmin(false);
          setAuthReady(true);
          return;
        }
        try {
          const session = (await authClient.auth.getSession()).data.session;
          if(!isCurrent())return;
          if (session?.user.id===nextUser.id) {
            const preferenceResponse = await withTimeout(monitoredFetch('/api/usage', {
              headers: { authorization: `Bearer ${session.access_token}` },
            }, 'load usage preference'), 10_000);
            if (preferenceResponse.ok) {
              const preference = await preferenceResponse.json() as { acknowledged?: boolean; sharingEnabled?: boolean };
              if(!isCurrent())return;
              if (preference.acknowledged) {
                const localSettings = await getSettings();
                if(!isCurrent())return;
                const synchronizedSettings = {
                  ...localSettings,
                  usageAnalyticsEnabled: Boolean(preference.sharingEnabled),
                  usageAnalyticsNoticeAcknowledged: true,
                };
                await saveSettings(synchronizedSettings);
              }
            }
          }
        } catch {
          // Keep the local preference as a fallback if the account-level service is temporarily unavailable.
        }
        if(!isCurrent())return;
        const resolvedDisplayName = resolveAccountName(data.display_name, authAccountName(nextUser));
        if (resolvedDisplayName && resolvedDisplayName !== data.display_name) {
          await authClient.from('profiles').update({ display_name: resolvedDisplayName, updated_at: new Date().toISOString() }).eq('user_id', nextUser.id);
        }
        if(!isCurrent())return;
        const billingRights = await fetchBillingRights(nextUser.id);
        if (!isCurrent()) return;
        setCloudExpiry(billingRights?.cloudExpiry??null);
        const entitledPlan: Plan = billingRights?.effectivePlan ?? (data.plan !== 'free' && data.plan_expires_at && new Date(data.plan_expires_at).getTime() <= Date.now() ? 'free' : data.plan);
        const nextProfile: AccountProfile = {
          userId: data.user_id,
          displayName: resolvedDisplayName,
          plan: entitledPlan,
          systemRole: data.system_role,
          accountStatus: data.account_status,
          cloudSyncEnabled: data.cloud_sync_enabled,
          onboardingCompleted: data.onboarding_completed,
          termsAcceptedAt: data.terms_accepted_at,
          planExpiresAt: data.plan_expires_at,
        };
        setProfile(nextProfile);
        setPlan(nextProfile.plan);
        setIsAdmin(nextProfile.systemRole === 'admin');
        if (nextProfile.plan !== 'free' && nextProfile.cloudSyncEnabled) {
          const localSettings = await getSettings();
          if(!isCurrent())return;
          try { await saveSettings({ ...localSettings, cloudSyncEnabled: true }); }
          catch(error){if(!(error instanceof DOMException)||error.name!=='QuotaExceededError')throw error;}
        }
        await refresh();
        if(!isCurrent())return;
        if (nextProfile.plan !== 'free' && nextProfile.cloudSyncEnabled) {
          // Open the local workspace immediately; a slow Cloud request must
          // not hold the whole application on the account loading screen.
          syncBusyRef.current = true;
          setSyncing(true);
          setSyncError('');
          void syncAllDocuments(nextUser, (stage) => { if (isCurrent()) setSyncStatus(syncStageLabel[stage]); })
            .then(async (result) => {
              if (!isCurrent()) return;
              const nextSettings = { ...result.settings, cloudSyncEnabled: true, lastSyncAt: new Date().toISOString() };
              let localSaved=result.localCacheSaved;
              try{await saveSettings(nextSettings);}
              catch(error){if(!(error instanceof DOMException)||error.name!=='QuotaExceededError')throw error;localSaved=false;}
              if(!isCurrent())return;
              setQuotes(result.quotes);setInvoices(result.invoices);setPayments(result.payments);setSettings(nextSettings);setTrashRecords(result.trash);
              if(!localSaved)setSyncError('อ่านข้อมูล Cloud ได้ แต่พื้นที่เครื่องไม่พอเก็บสำเนาออฟไลน์ กรุณาเพิ่มพื้นที่ก่อนแก้ไขข้อมูล ห้ามล้างข้อมูลที่ยังไม่ซิงค์');
              if(localSaved&&result.pendingLocalCount)window.setTimeout(()=>{if(isCurrent())syncAgainRef.current();},500);
            })
            .catch((reason) => {
              if (!isCurrent()) return;
              const stage = reason instanceof CloudSyncError ? reason.stage : 'download';
              setSyncError(`${syncStageLabel[stage]}: ${friendlySyncError(reason)}`);
              void reportClientError({ code: 'cloud_sync_failed', message: `${stage}: ${errorMessage(reason, 'sync failed')}` });
            })
            .finally(() => {
              if (isCurrent()) { syncBusyRef.current=false;setSyncing(false);setSyncStatus(''); }
            });
        }
      } catch (reason) {
        if(isCurrent())setInitializationError(friendlyAppError(reason instanceof Error ? reason.message : 'เตรียมบัญชีไม่สำเร็จ'));
      } finally {
        if (isCurrent()) setAuthReady(true);
      }
    };

    const { data: authListener } = authClient.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
      window.setTimeout(() => void prepareSession(session?.user ?? null), 0);
    });
    void withTimeout(authClient.auth.getSession(), 10_000)
      .then(({ data }) => {if(active&&!preparingUserId)return prepareSession(data.session?.user ?? null);})
      .catch(() => { if (active&&!preparingUserId) { activeUserRef.current=null;setUser(null); setAuthReady(true); } });
    return () => { active = false;sessionEpochRef.current++;cancelDocumentSyncs();authListener.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!authReady || !user || !supabase) return;
    const client = supabase, owner = user.id, epoch = sessionEpochRef.current;
    let active = true, generation = 0;
    const refresh = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== owner) return;
      const current = ++generation;
      const valid = () => active && generation === current && activeUserRef.current === owner && sessionEpochRef.current === epoch;
      void withTimeout(client.from('profiles').select('user_id,plan,plan_expires_at,account_status').eq('user_id', owner).single(), 10000).then(async ({ data, error }) => {
        if (!valid() || error || !data || data.user_id !== owner) return;
        const rights = await fetchBillingRights(owner);
        if (!valid()) return;
        if(rights)setCloudExpiry(rights.cloudExpiry??null);
        const expiry = data.plan_expires_at ? Date.parse(data.plan_expires_at) : null;
        const next: Plan = rights?.effectivePlan ?? (['pro','business'].includes(data.plan) && (expiry === null || (Number.isFinite(expiry) && expiry > Date.now())) ? data.plan : 'free');
        setPlan(next);
        setProfile((prior) => prior?.userId === owner ? { ...prior, plan: next, planExpiresAt: data.plan_expires_at, accountStatus: data.account_status } : prior);
        if (data.account_status === 'suspended') setIsAdmin(false);
        setNotice('อัปเดตสิทธิ์แพ็กเกจจากเซิร์ฟเวอร์แล้ว');
      }).catch(() => {});
    };
    window.addEventListener('bilaflow:billing-updated', refresh);
    return () => { active = false; window.removeEventListener('bilaflow:billing-updated', refresh); };
  }, [authReady, user]);

  useEffect(() => {
    if (!authReady || !user) return;
    const owner = user.id, epoch = sessionEpochRef.current, abort = new AbortController();
    let enabled = true, busy = false;
    const refresh = async () => {
      if (!enabled || busy || abort.signal.aborted || document.hidden) return;
      busy = true;
      try {
        const rights = await fetchBillingRights(owner, abort.signal);
        if (abort.signal.aborted || activeUserRef.current !== owner || sessionEpochRef.current !== epoch) return;
        if (!rights) return;
        setCloudExpiry(rights.cloudExpiry??null);
        setPlan(rights.effectivePlan);
        setProfile((prior) => prior?.userId === owner ? { ...prior, plan: rights.effectivePlan, planExpiresAt: rights.planExpiresAt } : prior);
      } finally { busy = false; }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60000);
    const visible = () => void refresh(); window.addEventListener('focus', visible); document.addEventListener('visibilitychange', visible);
    return () => { enabled = false; abort.abort(); window.clearInterval(timer); window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible); };
  }, [authReady, user?.id]);

  useEffect(() => {
    if (!authReady || !user || !supabase) return;
    const billingClient = supabase;
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (!checkout) return;
    let active = true;
    const clearCheckoutQuery = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };
    if (checkout === 'cancelled') {
      setNotice('ยกเลิกการชำระแล้ว ยังไม่มีการเรียกเก็บเงิน');
      clearCheckoutQuery();
      return;
    }
    if (checkout !== 'success' || !sessionId) {
      clearCheckoutQuery();
      return;
    }
    const confirmPayment = async () => {
      setNotice('กำลังยืนยันการชำระและเปิดสิทธิ์ Pro…');
      const session = (await billingClient.auth.getSession()).data.session;
      if (!session) throw new Error('ไม่พบเซสชันผู้ใช้');
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const response = await monitoredFetch(`/api/billing/status?session_id=${encodeURIComponent(sessionId)}`, {
          headers: { authorization: `Bearer ${session.access_token}` },
        }, 'confirm Pro payment');
        const result = await readApiJson<{ status?: string; reason?: string; isIntroTrial?: boolean; trialEndsAt?: string; plan?: Plan; planExpiresAt?: string; renewalMode?: 'manual' | 'automatic'; error?: string }>(response);
        if (!response.ok) throw new Error(result.error || 'ยืนยันการชำระไม่สำเร็จ');
        if (result.status === 'refunded') {
          if (!active) return;
          setPlan(result.plan ?? 'free'); setView('plans');
          setNotice('บิลนี้คืนเงินเต็มแล้ว สิทธิ์ของรอบนี้ยกเลิก โดยเก็บเอกสารและไฟล์ไว้'); clearCheckoutQuery(); return;
        }
        if (result.status === 'trial_denied' || result.status === 'expired') {
          if (!active) return;
          setView('plans');
          setNotice(result.status === 'expired' ? result.isIntroTrial ? 'สิทธิ์ทดลองสิ้นสุดแล้ว สามารถสมัคร Pro แบบจ่ายเงินได้' : 'หน้าชำระเงินหมดอายุแล้ว สามารถเริ่มรายการใหม่ได้'
            : result.reason === 'card_unverifiable' ? 'ตรวจสิทธิ์บัตรไม่ได้ จึงยังไม่เปิดทดลองและไม่เรียกเก็บเงิน คุณยังสมัคร Pro แบบจ่ายเงินได้'
              : 'บัญชีหรือบัตรนี้เคยใช้สิทธิ์ทดลองแล้ว จึงไม่เปิด Trial ซ้ำและไม่เรียกเก็บเงิน คุณยังสมัคร Pro แบบจ่ายเงินได้');
          clearCheckoutQuery();
          return;
        }
        if (result.status === 'active') {
          if (!active) return;
          const activatedPlan = result.plan ?? 'pro';
          setPlan(activatedPlan);
          setProfile((current) => current ? { ...current, plan: activatedPlan, planExpiresAt: result.planExpiresAt ?? current.planExpiresAt } : current);
          setView('plans');
          setNotice(result.isIntroTrial && result.trialEndsAt
            ? `เริ่มทดลอง Pro แล้ว หากไม่ยกเลิกก่อน ${shortDate(result.trialEndsAt)} จะตัดบัตร ฿249 และต่อทุก 31 วัน`
            : result.renewalMode === 'automatic'
            ? 'เปิดการต่ออัตโนมัติทุก 31 วันเรียบร้อยแล้ว'
            : 'เปิดใช้งาน BilaFlow Pro เรียบร้อยแล้ว');
          clearCheckoutQuery();
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1200));
      }
      if (active) setNotice('รับรายการชำระแล้ว ระบบกำลังตรวจสอบ กรุณารีเฟรชอีกครั้งในอีกสักครู่');
      clearCheckoutQuery();
    };
    void confirmPayment().catch(() => {
      if (active) setNotice('ตรวจสอบการชำระไม่สำเร็จ กรุณาติดต่อผู้ดูแลพร้อมอีเมลบัญชี');
      clearCheckoutQuery();
    });
    return () => { active = false; };
  }, [authReady, user]);

  useEffect(() => {
    if (!supabase || !user || !profile?.onboardingCompleted || !settings.usageAnalyticsNoticeAcknowledged) return;
    const usageClient = supabase;
    const activePayments = payments.filter((payment) => !payment.voidedAt);
    const allDates = [
      ...quotes.map((quote) => quote.updatedAt),
      ...invoices.map((invoice) => invoice.updatedAt),
      ...activePayments.map((payment) => payment.createdAt),
    ].filter(Boolean);
    const lastActivityAt = allDates.length
      ? new Date(Math.max(...allDates.map((value) => new Date(value).getTime()))).toISOString()
      : null;
    const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const recentDocumentCount = [
      ...quotes.map((quote) => quote.createdAt),
      ...invoices.map((invoice) => invoice.createdAt),
      ...activePayments.map((payment) => payment.createdAt),
    ].filter((value) => new Date(value).getTime() >= cutoff).length;
    const sharingEnabled = settings.usageAnalyticsEnabled;
    const report = {
      sharingEnabled,
      quoteCount: sharingEnabled ? quotes.length : 0,
      invoiceCount: sharingEnabled ? invoices.length : 0,
      receiptCount: sharingEnabled ? activePayments.length : 0,
      recentDocumentCount: sharingEnabled ? recentDocumentCount : 0,
      lastActivityAt: sharingEnabled ? lastActivityAt : null,
      appVersion: '0.1.0',
    };
    const reportKey = JSON.stringify(report);
    if (usageReportKeyRef.current === reportKey) return;
    const timer = window.setTimeout(async () => {
      const session = (await usageClient.auth.getSession()).data.session;
      if (!session) return;
      const response = await monitoredFetch('/api/usage', {
        method: 'POST',
        headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify(report),
      }, 'report usage metrics');
      if (response.ok) usageReportKeyRef.current = reportKey;
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [invoices, payments, profile?.onboardingCompleted, quotes, settings.usageAnalyticsEnabled, settings.usageAnalyticsNoticeAcknowledged, user]);

  const filteredQuotes = useMemo(() => quotes.filter((quote) => {
    const matchesText = `${quote.number} ${quote.customerName}`.toLowerCase().includes(search.toLowerCase());
    return matchesText && (statusFilter === 'all' || quote.status === statusFilter);
  }), [quotes, search, statusFilter]);

  const filteredInvoices = useMemo(() => invoices.filter((invoice) => `${invoice.number} ${invoice.quoteSnapshot.number} ${invoice.quoteSnapshot.customerName}`.toLowerCase().includes(search.toLowerCase())), [invoices, search]);

  const customers = useMemo<CustomerProfile[]>(() => {
    const unique = new Map<string, CustomerProfile>();
    for (const quote of [...quotes].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))) {
      const key = quote.customerTaxId?.trim() || quote.customerName.trim().toLocaleLowerCase('th-TH');
      if (!key || unique.has(key)) continue;
      unique.set(key, {
        type: quote.customerType ?? 'company',
        name: quote.customerName,
        taxId: quote.customerTaxId ?? '',
        branchType: quote.customerBranchType ?? 'headOffice',
        branchCode: quote.customerBranchCode ?? '',
        contactName: quote.customerContactName ?? '',
        email: quote.customerEmail ?? '',
        phone: quote.customerPhone ?? '',
        address: quote.customerAddress ?? '',
      });
    }
    return [...unique.values()];
  }, [quotes]);

  const metrics = useMemo(() => ({
    total: quotes.reduce((sum, quote) => sum + quoteTotal(quote), 0),
    received: invoices.reduce((sum, invoice) => sum + invoicePaidAmount(invoice.id, payments), 0),
    outstanding: invoices.reduce((sum, invoice) => sum + invoiceBalance(invoice, payments), 0),
    paidInvoices: invoices.filter((invoice) => invoiceStatus(invoice, payments) === 'paid').length,
  }), [invoices, payments, quotes]);
  const receivables = useMemo(() => buildReceivablesReport(invoices, payments), [invoices, payments]);

  const currentBackupSize = useMemo(() => {
    const invoiceIds = new Set(invoices.map((invoice) => invoice.id));
    return backupSizeBytes({
      version: 3,
      quotes,
      invoices,
      payments: payments.filter((payment) => invoiceIds.has(payment.invoiceId)),
      settings,
    });
  }, [invoices, payments, quotes, settings]);
  const currentBackupPercent = Math.min(100, (currentBackupSize / MAX_BACKUP_BYTES) * 100);
  const currentBackupLevel = backupUsageLevel(currentBackupSize);
  const backupBlocked = currentBackupSize >= MAX_BACKUP_BYTES;
  const missingFileCount = useMemo(() => {
    const invoiceIds = new Set(invoices.map((invoice) => invoice.id));
    const keys = missingCloudFileKeys([quotes, invoices, payments.filter((payment) => invoiceIds.has(payment.invoiceId)), settings.company]);
    if (Array.isArray(settings.cloudMissingFileKeys)) settings.cloudMissingFileKeys.forEach((key) => keys.add(key));
    return keys.size;
  }, [quotes, invoices, payments, settings.company, settings.cloudMissingFileKeys]);

  const backupReminderDue = useMemo(() => {
    if (plan !== 'free' || (quotes.length === 0 && invoices.length === 0)) return false;
    const now = Date.now();
    const lastBackup = settings.lastBackupAt ? new Date(settings.lastBackupAt).getTime() : 0;
    const dismissed = settings.backupReminderDismissedAt ? new Date(settings.backupReminderDismissedAt).getTime() : 0;
    const backupIsOld = !Number.isFinite(lastBackup) || lastBackup === 0 || now - lastBackup >= 7 * 24 * 60 * 60 * 1000;
    const dismissalExpired = !Number.isFinite(dismissed) || dismissed === 0 || now - dismissed >= 3 * 24 * 60 * 60 * 1000;
    return backupIsOld && dismissalExpired;
  }, [plan, quotes.length, invoices.length, settings.lastBackupAt, settings.backupReminderDismissedAt]);

  const nextNumber = useMemo(() => {
    const year = new Date().getFullYear();
    const current = settings.quoteSequenceYear === year ? settings.lastQuoteSequence : 0;
    return `QT-${year}-${String(current + 1).padStart(4, '0')}`;
  }, [settings.lastQuoteSequence, settings.quoteSequenceYear]);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 3200);
  };

  const handleSaveQuote = async (quote: Quote) => {
    let quoteToSave = quote;
    if ((editorMode === 'new' || editorMode === 'copy') && quote.number === nextNumber) {
      const reserved = await reserveDocumentNumber('quote', new Date(quote.issueDate));
      quoteToSave = { ...quote, number: reserved.number };
    }
    await saveQuote(quoteToSave);
    if (quoteToSave.revisedFromId) {
      const original = quotes.find((item) => item.id === quoteToSave.revisedFromId);
      if (original && original.replacedById !== quoteToSave.id) {
        await saveQuote({ ...original, status: 'replaced', replacedById: quoteToSave.id, updatedAt: new Date().toISOString(), version: original.version + 1, syncState: 'pending' });
      }
    }
    const year = new Date(quoteToSave.issueDate).getFullYear();
    const prefix = `QT-${year}-`;
    const sequence = quoteToSave.number.startsWith(prefix) ? Number(quoteToSave.number.slice(prefix.length)) || 0 : 0;
    const storedSettings = await getSettings();
    if (sequence > (storedSettings.quoteSequenceYear === year ? storedSettings.lastQuoteSequence : 0)) {
      await saveSettings({ ...storedSettings, quoteSequenceYear: year, lastQuoteSequence: sequence });
    }
    setEditorQuote(undefined);
    await refresh();
    flash(quoteToSave.revisedFromId ? `สร้างฉบับแก้ไข ${quoteToSave.number} แล้ว` : `บันทึก ${quoteToSave.number} ในอุปกรณ์แล้ว`);
    if (cloudActive && user) void runSync();
  };

  const openNewQuote = () => {
    setEditorMode('new');
    setEditorQuote(null);
  };

  const openEditQuote = (quote: Quote) => {
    if (quote.status === 'replaced') {
      const replacement = quotes.find((item) => item.id === quote.replacedById);
      if (replacement) setPreviewQuote(replacement);
      else flash('เอกสารนี้ถูกแทนที่ด้วยฉบับแก้ไขแล้ว');
      return;
    }
    if (quote.status === 'accepted') {
      if (!window.confirm(`${quote.number} ถูกล็อกหลังลูกค้ายอมรับแล้ว ต้องการสร้างฉบับแก้ไขใหม่หรือไม่?`)) return;
      setEditorMode('revision');
      setEditorQuote(createRevision(quote, quotes));
      return;
    }
    setEditorMode('edit');
    setEditorQuote(quote);
  };

  const openQuoteCopy = (quote: Quote) => {
    const now = new Date();
    setEditorMode('copy');
    setEditorQuote({
      ...structuredClone(quote),
      id: crypto.randomUUID(),
      number: nextNumber,
      issueDate: thaiDate(now),
      expiryDate: thaiDateAfter(14,now),
      status: 'draft',
      acceptance: null,
      revision: 0,
      revisedFromId: null,
      replacedById: null,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      version: 1,
      deletedAt: null,
      syncState: 'pending',
    });
  };

  const handleDelete = async (quote: Quote) => {
    if(syncBusyRef.current||trashWorking){flash('กรุณารอการซิงค์ปัจจุบันให้เสร็จก่อน');return;}
    if (!window.confirm(`ย้าย ${quote.number} เข้าถังขยะใช่ไหม? กู้คืนได้ภายใน 24 ชั่วโมง หลังจากนั้นระบบจะลบเนื้อหาถาวรในรอบตรวจรายชั่วโมง`)) return;
    if(cloudActive&&user){await performTrashAction({...quote,kind:'quote'},'trash');return;}
    await markQuoteDeleted(quote.id,plan==='free');
    await refresh();
    flash('ย้ายเข้าถังขยะในเครื่องแล้ว กู้คืนได้ภายใน 24 ชั่วโมง');
    if (cloudActive && user) void runSync();
  };

  const updateStatus = async (quote: Quote, status: QuoteStatus) => {
    if (quote.status === 'accepted' || quote.status === 'replaced') return;
    if (status === 'accepted') { setAcceptanceTarget({ quote, action: 'status' }); return; }
    if (status === 'replaced') return;
    await saveQuote({ ...quote, status, acceptance: null, updatedAt: new Date().toISOString(), version: quote.version + 1, syncState: 'pending' });
    await refresh();
    if (cloudActive && user) void runSync();
  };

  const createInvoiceFromAcceptedQuote = async (quote: Quote) => {
    const now = new Date();
    const reserved = await reserveDocumentNumber('invoice', now);
    const invoice = createInvoiceRecord(quote, reserved.number, now);
    await saveInvoice(invoice);
    await refresh();
    setManagerInvoice(invoice);
    flash(`สร้าง ${invoice.number} แล้ว`);
    if (cloudActive && user) void runSync();
  };

  const openOrCreateInvoice = async (quote: Quote) => {
    const existing = invoices.find((invoice) => invoice.quoteId === quote.id);
    if (existing) { setManagerInvoice(existing); return; }
    if (quote.status !== 'accepted' || !quote.acceptance) {
      setAcceptanceTarget({ quote, action: 'invoice' });
      return;
    }
    await createInvoiceFromAcceptedQuote(quote);
  };

  const recordAcceptance = async (acceptance: QuoteAcceptance) => {
    if (!acceptanceTarget || acceptanceTarget.action === 'view') return;
    const acceptedQuote: Quote = {
      ...acceptanceTarget.quote,
      status: 'accepted',
      acceptance,
      updatedAt: new Date().toISOString(),
      version: acceptanceTarget.quote.version + 1,
      syncState: 'pending',
    };
    await saveQuote(acceptedQuote);
    const action = acceptanceTarget.action;
    setAcceptanceTarget(null);
    await refresh();
    if (cloudActive && user) void runSync();
    flash(`บันทึกการยอมรับของ ${acceptance.acceptedBy} แล้ว`);
    if (action === 'invoice') await createInvoiceFromAcceptedQuote(acceptedQuote);
  };

  const addPayment = async (invoice: Invoice, draft: Pick<Payment, 'paidAt' | 'amount' | 'method' | 'reference' | 'note' | 'attachment'>) => {
    const currentPayments = await listPayments();
    const paidAt = new Date(draft.paidAt);
    if (Number.isNaN(paidAt.getTime())) throw new Error('วันที่รับชำระไม่ถูกต้อง');
    const reserved = await reserveDocumentNumber('receipt', paidAt);
    const { payment, nextStatus } = createPaymentRecord(invoice, currentPayments, draft, reserved.number);
    await savePayment(payment);
    await saveInvoice({ ...invoice, status: nextStatus, updatedAt: new Date().toISOString(), version: invoice.version + 1, syncState: 'pending' });
    await refresh();
    flash(`บันทึกรับชำระและสร้าง ${payment.receiptNumber} แล้ว`);
    if (cloudActive && user) void runSync();
  };

  const voidPayment = async (invoice: Invoice, payment: Payment) => {
    if (!window.confirm(`ยกเลิกรายการ ${payment.receiptNumber} ใช่ไหม? รายการจะยังอยู่ในประวัติและไม่ถูกนำไปรวมยอด`)) return;
    const voided = { ...payment, voidedAt: new Date().toISOString() };
    const nextPayments = payments.map((item) => item.id === payment.id ? voided : item);
    await savePayment(voided);
    await saveInvoice({ ...invoice, status: invoiceStatus(invoice, nextPayments), updatedAt: new Date().toISOString(), version: invoice.version + 1, syncState: 'pending' });
    await refresh();
    flash(`ยกเลิก ${payment.receiptNumber} แล้ว โดยเก็บประวัติไว้`);
    if (cloudActive && user) void runSync();
  };

  const archiveInvoice = async (invoice: Invoice) => {
    if(syncBusyRef.current||trashWorking){flash('กรุณารอการซิงค์ปัจจุบันให้เสร็จก่อน');return;}
    if (payments.some((payment) => payment.invoiceId === invoice.id && !payment.voidedAt)) { flash('ต้องยกเลิกรายการรับเงินก่อนนำใบแจ้งหนี้ออก'); return; }
    if (!window.confirm(`ย้าย ${invoice.number} เข้าถังขยะ 24 ชั่วโมงใช่ไหม? หลังหมดเวลากู้จะลบเนื้อหาถาวร ใบเสนอราคาต้นทางจะยังอยู่`)) return;
    if(cloudActive&&user){await performTrashAction({...invoice,kind:'invoice'},'trash');setManagerInvoice(null);return;}
    await markInvoiceDeleted(invoice.id,plan==='free');
    setManagerInvoice(null);
    await refresh();
    flash(`ย้าย ${invoice.number} เข้าถังขยะในเครื่องแล้ว กู้คืนได้ภายใน 24 ชั่วโมง`);
    if (cloudActive && user) void runSync();
  };

  const updateInvoiceDueDate = async (invoice: Invoice, dueDate: string) => {
    if (!dueDate || dueDate < invoice.issueDate) { flash('วันครบกำหนดต้องไม่ก่อนวันที่ออกใบแจ้งหนี้'); return; }
    await saveInvoice({ ...invoice, dueDate, updatedAt: new Date().toISOString(), version: invoice.version + 1, syncState: 'pending' });
    await refresh();
    flash('อัปเดตวันครบกำหนดแล้ว');
    if (cloudActive && user) void runSync();
  };

  const runSync = async () => {
    if (syncBusyRef.current) return;
    if (plan === 'free') { setView('plans'); flash('Cloud Sync เป็นฟีเจอร์ของแพ็กเกจ Pro'); return; }
    if (!user) { setView('settings'); flash('กรุณาเข้าสู่ระบบก่อนเปิด Cloud Sync'); return; }
    if (!supabase) { setView('settings'); flash('กรุณาตั้งค่า Supabase ในไฟล์ .env'); return; }
    const syncClient = supabase;
    const syncEpoch=sessionEpochRef.current;
    const isCurrent=()=>activeUserRef.current===user.id&&sessionEpochRef.current===syncEpoch;
    syncBusyRef.current = true;
    setSyncing(true);
    setSyncError('');
    let stage: SyncStage = 'local';
    try {
      const result = await syncAllDocuments(user, (nextStage) => { stage = nextStage;if(activeUserRef.current===user.id)setSyncStatus(syncStageLabel[nextStage]); });
      if (!isCurrent()) return;
      if (!profile?.cloudSyncEnabled) {
        stage = 'enable';
        setSyncStatus(syncStageLabel.enable);
        const { data, error: profileError } = await withDatabaseRetry((signal) => syncClient.from('profiles')
          .update({ cloud_sync_enabled: true }).eq('user_id', user.id).select('cloud_sync_enabled').abortSignal(signal).single(), { maxAttempts: 1, timeoutMs: 8_000 });
        if (profileError) throw profileError;
        if (!data?.cloud_sync_enabled) throw new Error('Cloud Sync ยังเปิดไม่สำเร็จ');
        if(!isCurrent())return;
        setProfile((current) => current ? { ...current, cloudSyncEnabled: true } : current);
      }
      const nextSettings = { ...result.settings, cloudSyncEnabled: true, lastSyncAt: new Date().toISOString() };
      if (!isCurrent()) return;
      let localSaved = result.localCacheSaved;
      try { await saveSettings(nextSettings); }
      catch (error) { if (!(error instanceof DOMException) || error.name !== 'QuotaExceededError') throw error; localSaved = false; }
      if (!isCurrent()) return;
      setQuotes(result.quotes); setInvoices(result.invoices); setPayments(result.payments); setSettings(nextSettings);setTrashRecords(result.trash);
      if (!localSaved) {
        setSyncError('ข้อมูล Cloud เปิดใช้ได้ในรอบนี้ แต่พื้นที่เครื่องไม่พอเก็บสำเนาออฟไลน์ กรุณาเพิ่มพื้นที่ก่อนแก้ไขข้อมูล ห้ามล้างข้อมูลที่ยังไม่ซิงค์');
        flash('อ่านข้อมูล Cloud แล้ว แต่ยังเก็บสำเนาออฟไลน์ไม่ครบเพราะพื้นที่เครื่องเต็ม');
      } else flash(result.missingFileCount ? `ซิงก์ข้อมูลเอกสารแล้ว แต่ยังมีไฟล์แนบสูญหาย ${result.missingFileCount} ไฟล์ ไม่ได้กู้ไฟล์ต้นฉบับกลับมา` : result.pendingLocalCount ? 'ซิงค์รอบนี้แล้ว ยังมีการแก้ไขใหม่รอซิงค์' : 'ซิงค์ข้อมูลแล้ว ไฟล์ Cloud จะโหลดเมื่อเปิดใช้');
      if(localSaved&&result.pendingLocalCount)window.setTimeout(()=>{if(isCurrent())void runSync();},500);
    } catch (error) {
      if (!isCurrent()) return;
      const failedStage = error instanceof CloudSyncError ? error.stage : stage;
      const message = `${syncStageLabel[failedStage]}: ${friendlySyncError(error)}`;
      setSyncError(message);
      flash(message);
      void reportClientError({ code: 'cloud_sync_failed', message: `${failedStage}: ${errorMessage(error, 'sync failed')}` });
    } finally {
      if(isCurrent()){
        syncBusyRef.current = false;
        setSyncing(false);
        setSyncStatus('');
      }
    }
  };

  syncAgainRef.current=()=>{void runSync();};
  const performTrashAction=async(record:TrashItem,action:'trash'|'restore')=>{
    if(trashWorking||syncBusyRef.current){flash('กรุณารอการซิงค์ปัจจุบันให้เสร็จก่อน');return;}
    setTrashWorking(true);
    try{
      if(!cloudActive||!user){
        if(action==='restore'){await restoreLocalDocument(record.kind,record.id);await refresh();flash('กู้คืนเอกสารในเครื่องแล้ว');}
        return;
      }
      if(!navigator.onLine)throw new Error('cloud_restore_requires_online');
      syncBusyRef.current=true;setSyncing(true);setSyncStatus(action==='trash'?'กำลังย้ายเข้าถังขยะ':'กำลังกู้คืนเอกสาร');
      let revision=record.cloudRevision;
      if(record.syncState!=='synced'||revision===undefined){
        const synced=await syncAllDocuments(user);const stored=record.kind==='quote'?[...synced.quotes,...synced.trash.filter(value=>value.kind==='quote')]:[...synced.invoices,...synced.trash.filter(value=>value.kind==='invoice')];
        revision=stored.find(value=>value.id===record.id)?.cloudRevision;
      }
      if(revision===undefined)throw new Error('cloud_document_not_found');
      const session=(await supabase?.auth.getSession())?.data.session;
      if(!session||session.user.id!==user.id||activeUserRef.current!==user.id)throw new Error('Session changed');
      await cloudTrashAction(record.id,revision,action,{userId:user.id,token:session.access_token,signal:AbortSignal.timeout(35_000)});
      const result=await syncAllDocuments(user);
      if(activeUserRef.current!==user.id)return;
      setQuotes(result.quotes);setInvoices(result.invoices);setPayments(result.payments);setSettings(result.settings);setTrashRecords(result.trash);
      if(!result.localCacheSaved)setSyncError('ข้อมูล Cloud พร้อมใช้ แต่พื้นที่เครื่องไม่พอเก็บสำเนาออฟไลน์ให้ครบ กรุณาเพิ่มพื้นที่ก่อนแก้ไขข้อมูล');
      flash(action==='trash'?'ย้ายเข้าถังขยะ Cloud แล้ว กู้คืนได้ภายใน 24 ชั่วโมง':'กู้คืนเอกสารและไฟล์อ้างอิงแล้ว');
    }catch(error){if(!user||activeUserRef.current===user.id)flash(friendlySyncError(error));}
    finally{if(!user||activeUserRef.current===user.id){setTrashWorking(false);syncBusyRef.current=false;setSyncing(false);setSyncStatus('');}}
  };

  const toggleCloud = async () => {
    if (syncBusyRef.current) return;
    if (!settings.cloudSyncEnabled) { await runSync(); return; }
    if (supabase && user) {
      const profileClient = supabase;
      const { error } = await withDatabaseRetry((signal) => profileClient.from('profiles').update({ cloud_sync_enabled: false }).eq('user_id', user.id).abortSignal(signal));
      if (error) { flash(friendlySyncError(error)); return; }
    }
    const nextSettings = { ...settings, cloudSyncEnabled: false };
    await saveSettings(nextSettings);
    setSettings(nextSettings);
    setProfile((current) => current ? { ...current, cloudSyncEnabled: false } : current);
    setSyncError('');
    flash('ปิด Cloud Sync แล้ว ข้อมูลในเครื่องยังอยู่ครบ');
  };

  const exportBackup = async (label = 'backup') => {
    const exportedAt = new Date().toISOString();
    const nextSettings = { ...settings, lastBackupAt: exportedAt, backupReminderDismissedAt: null };
    const liveInvoiceIds = new Set(invoices.map((invoice) => invoice.id));
    const exportedPayments = payments.filter((payment) => liveInvoiceIds.has(payment.invoiceId));
    let payload = { version: 3 as const, exportedAt, quotes, invoices, payments: exportedPayments, settings: nextSettings };
    try { payload = await hydrateBackupFiles(payload, MAX_BACKUP_BYTES); }
    catch (error) { flash(errorMessage(error, 'โหลดไฟล์ Cloud สำหรับสำรองไม่ครบ จึงยังไม่ส่งออก')); return false; }
    if (backupSizeBytes(payload) >= MAX_BACKUP_BYTES) {
      flash('ไม่สามารถสำรองข้อมูลได้ เพราะไฟล์เกิน 100 MB กรุณาลดไฟล์แนบก่อน');
      return false;
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bilaflow-${label}-${thaiDate()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    await saveSettings(nextSettings);
    setSettings(nextSettings);
    if (label === 'backup') flash(missingFileCount ? `ส่งออกข้อมูลแล้ว แต่ไม่มีต้นฉบับของไฟล์ที่สูญหาย ${missingFileCount} ไฟล์ในไฟล์สำรองนี้` : 'ดาวน์โหลดไฟล์สำรองแล้ว ระบบจะเตือนอีกครั้งใน 7 วัน');
    return true;
  };

  const dismissBackupReminder = async () => {
    const nextSettings = { ...settings, backupReminderDismissedAt: new Date().toISOString() };
    await saveSettings(nextSettings);
    setSettings(nextSettings);
  };

  const importBackup = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (file.size > MAX_BACKUP_BYTES) throw new Error('ไฟล์สำรองมีขนาดเกิน 100 MB');
      const data = parseBackup(await file.text());
      const importedInvoices = data.invoices;
      const importedPayments = data.payments;
      if (!window.confirm(`พบใบเสนอราคา ${data.quotes.length} ใบ ใบแจ้งหนี้ ${importedInvoices.length} ใบ และรายการรับชำระ ${importedPayments.length} รายการ การนำเข้าจะแทนที่ข้อมูลในอุปกรณ์นี้ ต้องการดำเนินการต่อหรือไม่?`)) return;
      if (!await exportBackup('before-import')) throw new Error('นำเข้าข้อมูลไม่ได้ เพราะข้อมูลเดิมเกิน 100 MB และยังสำรองก่อนนำเข้าไม่สำเร็จ');
      await replaceLocalData(data.quotes, data.settings, importedInvoices, importedPayments);
      await refresh();
      flash('นำเข้าข้อมูลและหลักฐานการชำระเรียบร้อยแล้ว');
    } catch (error) {
      flash(error instanceof Error ? error.message : 'นำเข้าไฟล์ไม่สำเร็จ');
    } finally {
      event.target.value = '';
    }
  };

  const saveCompany = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextSettings = { ...settings, cloudSettingsUpdatedAt: new Date().toISOString() };
    await saveSettings(nextSettings);
    setSettings(nextSettings);
    flash('บันทึกข้อมูลบริษัทแล้ว');
    if (cloudActive && user) void runSync();
  };

  const saveUsagePreferenceToAccount = async (sharingEnabled: boolean) => {
    if (!supabase) throw new Error('ระบบบัญชียังไม่พร้อมใช้งาน');
    const session = (await supabase.auth.getSession()).data.session;
    if (!session) throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
    const response = await withTimeout(monitoredFetch('/api/usage', {
      method: 'POST',
      headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ sharingEnabled, appVersion: '0.1.0' }),
    }, 'save usage preference'), 10_000);
    if (!response.ok) throw new Error('บันทึกตัวเลือกกับบัญชีไม่สำเร็จ กรุณาลองใหม่');
  };

  const toggleUsageAnalytics = async () => {
    const nextSettings = { ...settings, usageAnalyticsEnabled: !settings.usageAnalyticsEnabled, usageAnalyticsNoticeAcknowledged: true };
    try {
      await saveUsagePreferenceToAccount(nextSettings.usageAnalyticsEnabled);
      await saveSettings(nextSettings);
      usageReportKeyRef.current = '';
      setSettings(nextSettings);
      flash(nextSettings.usageAnalyticsEnabled ? 'เปิดการแชร์สถิติการใช้งานแล้ว' : 'ปิดการแชร์สถิติและล้างตัวเลขที่รายงานแล้ว');
    } catch (reason) {
      flash(reason instanceof Error ? reason.message : 'บันทึกตัวเลือกไม่สำเร็จ');
    }
  };

  const acknowledgeUsageAnalytics = async (sharingEnabled: boolean) => {
    const nextSettings = { ...settings, usageAnalyticsEnabled: sharingEnabled, usageAnalyticsNoticeAcknowledged: true };
    await saveUsagePreferenceToAccount(sharingEnabled);
    await saveSettings(nextSettings);
    usageReportKeyRef.current = '';
    setSettings(nextSettings);
    flash(sharingEnabled ? 'รับทราบและเปิดการแชร์สถิติจำนวนรวมแล้ว' : 'ปิดการแชร์สถิติการใช้งานแล้ว');
  };

  const deleteAccount = async (reasonCategory: string, reasonDetails: string, confirmationEmail: string) => {
    if (!supabase || !user) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
    const session = (await supabase.auth.getSession()).data.session;
    if (!session) throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง');
    const response = await monitoredFetch('/api/account/delete', {
      method: 'POST',
      headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ reasonCategory, reasonDetails, confirmationEmail }),
    }, 'delete account');
    const result = await response.json().catch(() => ({ error: 'invalid_response' })) as { error?: string; migrationRequired?: boolean };
    if (!response.ok) {
      window.dispatchEvent(new CustomEvent('bilaflow:checkout-pending-updated', { detail: user.id }));
      if (result.migrationRequired) throw new Error('ยังไม่ได้ติดตั้ง migration ระบบลบบัญชี');
      if (result.error === 'privileged_account') throw new Error('บัญชีผู้ดูแลต้องโอนสิทธิ์ก่อนลบ กรุณาติดต่อ privacy@bilaflow.com');
      if (result.error === 'confirmation_mismatch') throw new Error('อีเมลยืนยันไม่ตรงกับบัญชี');
      if (result.error === 'reauthentication_required') throw new Error('เพื่อความปลอดภัย กรุณาออกจากระบบแล้วเข้าสู่ระบบใหม่ก่อนลบบัญชี');
      if (result.error === 'account_billing_pending') throw new Error('ยังมีรายการชำระเงินหรือเปลี่ยนแพ็กเกจค้างอยู่ กรุณาไปหน้าแพ็กเกจเพื่อตรวจผลหรือยกเลิกรายการก่อนลบบัญชี ข้อมูลยังไม่ถูกลบ');
      if (result.error === 'account_cloud_pending') throw new Error('ระบบหยุดรับงาน Cloud ใหม่แล้ว และกำลังรอการซิงค์ที่เริ่มไปก่อนหน้า กรุณารอสักครู่แล้วลองลบบัญชีอีกครั้ง ยังไม่ได้เริ่มลบไฟล์');
      if (result.error === 'account_cloud_requires_review') throw new Error('มีงาน Cloud ที่ยังยืนยันผลไม่ได้ ระบบพักการลบไว้เพื่อไม่ให้ไฟล์กลับมาหลังลบ กรุณาติดต่อ privacy@bilaflow.com');
      if (result.error === 'account_deletion_incomplete') throw new Error('การลบบัญชียังไม่เสร็จ ระบบพักการเริ่มบิลใหม่ไว้แล้ว กรุณาลองลบบัญชีอีกครั้ง หรือติดต่อ privacy@bilaflow.com');
      throw new Error('ลบบัญชีไม่สำเร็จ กรุณาลองใหม่หรือติดต่อ privacy@bilaflow.com');
    }
    await Promise.race([
      deleteLocalDataForOwner(user.id),
      new Promise<void>((resolve) => window.setTimeout(resolve, 2000)),
    ]).catch(() => undefined);
    localStorage.removeItem(`quoteflow:legacy-migration:${user.id}`);
    await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
    window.location.replace('/');
  };

  const completeOnboarding = async (nextSettings: AppSettings) => {
    if (!supabase || !user) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
    const onboardingSettings = { ...nextSettings, cloudSettingsUpdatedAt: new Date().toISOString() };
    await saveSettings(onboardingSettings);
    let acceptedAt = profile?.termsAcceptedAt ?? null;
    if (!acceptedAt) {
      const { data: consentAt, error: consentError } = await withTimeout(supabase.rpc('accept_current_policies'));
      if (consentError) throw consentError;
      acceptedAt = typeof consentAt === 'string' ? consentAt : new Date().toISOString();
    }
    const accountName = resolveAccountName(profile?.displayName, authAccountName(user), nextSettings.company.contactName, nextSettings.company.name, user.email?.split('@')[0]);
    const { error } = await withTimeout(supabase.from('profiles').update({
      onboarding_completed: true,
      display_name: accountName,
      updated_at: new Date().toISOString(),
    }).eq('user_id', user.id));
    if (error) throw error;
    setSettings(onboardingSettings);
    setProfile((current) => current ? { ...current, onboardingCompleted: true, displayName: accountName, termsAcceptedAt: acceptedAt } : current);
    flash('ตั้งค่าบัญชีเรียบร้อยแล้ว');
  };

  const signOut = async () => {
    activeUserRef.current=null;sessionEpochRef.current++;cancelDocumentSyncs();setLocalDataOwner(null);
    if (supabase) await withTimeout(supabase.auth.signOut(), 10_000).catch(() => undefined);
    setLocalDataOwner(null);
  };

  if (!authReady) return <div className="app-loading"><div className="brand-mark">BF</div><RefreshCw className="spin" size={22} /><span>กำลังเตรียมพื้นที่ของคุณ…</span></div>;
  if (!isSupabaseConfigured || recoveryMode) return <AuthScreen configured={isSupabaseConfigured} recoveryMode={recoveryMode} onRecoveryComplete={() => setRecoveryMode(false)} />;
  if (!user) {
    if (publicRoute === 'landing') return <PublicLanding
      onSignIn={() => { setAuthIntent('signin'); setPublicRoute('auth'); window.scrollTo({ top: 0 }); }}
      onStartFree={() => { setAuthIntent('signup'); setPublicRoute('auth'); window.scrollTo({ top: 0 }); }}
    />;
    return <AuthScreen configured recoveryMode={false} initialMode={authIntent} onBack={() => setPublicRoute('landing')} onRecoveryComplete={() => setRecoveryMode(false)} />;
  }
  if (initializationError) return <main className="auth-shell setup-required"><section className="auth-brand-panel"><div className="auth-brand"><span>BF</span><strong>BilaFlow</strong></div><div><span className="auth-kicker">Account setup</span><h1>เตรียมพื้นที่บัญชียังไม่สำเร็จ</h1><p>บัญชีของคุณปลอดภัยและข้อมูลเดิมยังไม่ถูกลบ</p></div></section><section className="auth-card"><div className="auth-card-heading"><ShieldCheck size={25} /><div><h2>เชื่อมต่อระบบไม่สำเร็จ</h2><p>{initializationError}</p></div></div><div className="auth-recovery-actions"><button className="btn primary" onClick={() => window.location.reload()}><RefreshCw size={16} /> ลองใหม่</button><button className="btn ghost" onClick={() => void signOut()}>ออกจากระบบ</button></div></section></main>;
  if (profile?.accountStatus === 'suspended') return <main className="auth-shell setup-required"><section className="auth-brand-panel"><div className="auth-brand"><span>BF</span><strong>BilaFlow</strong></div><div><span className="auth-kicker">Account status</span><h1>บัญชีนี้ถูกระงับการใช้งาน</h1><p>เอกสารในอุปกรณ์ยังไม่ถูกลบ กรุณาติดต่อผู้ดูแลระบบเพื่อตรวจสอบบัญชี</p></div></section><section className="auth-card"><button className="btn ghost" onClick={() => void signOut()}>ออกจากระบบ</button></section></main>;
  if (!settings.usageAnalyticsNoticeAcknowledged) return <UsageAnalyticsChoiceScreen email={user.email ?? ''} onChoose={acknowledgeUsageAnalytics} onSignOut={() => void signOut()} />;
  if (profile && !profile.onboardingCompleted) return <OnboardingScreen email={user.email ?? ''} settings={settings} requiresConsent={!profile.termsAcceptedAt} onComplete={completeOnboarding} onSignOut={() => void signOut()} />;

  return (
    <div className="app-shell">
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="brand"><div className="brand-mark">BF</div><div><strong>BilaFlow</strong><span>เอกสารที่เป็นของคุณ</span></div><button className="icon-btn mobile-close" onClick={() => setMenuOpen(false)}><X size={20} /></button></div>
        <nav>
          <button className={view === 'dashboard' ? 'active' : ''} onClick={() => { setView('dashboard'); setMenuOpen(false); }}><LayoutDashboard size={19} /> ภาพรวม</button>
          <button className={view === 'quotes' ? 'active' : ''} onClick={() => { setView('quotes'); setMenuOpen(false); }}><FileText size={19} /> ใบเสนอราคา <span className="nav-count">{quotes.length}</span></button>
          <button className={view === 'invoices' ? 'active' : ''} onClick={() => { setView('invoices'); setMenuOpen(false); }}><ReceiptText size={19} /> ใบแจ้งหนี้และรับเงิน <span className="nav-count">{invoices.length}</span></button>
          <button className={view === 'reports' ? 'active' : ''} onClick={() => { setView('reports'); setMenuOpen(false); }}><CircleDollarSign size={19} /> รายงานลูกหนี้ {plan === 'free' ? <span className="nav-pro">PRO</span> : receivables.overdueCount > 0 ? <span className="nav-alert">{receivables.overdueCount}</span> : null}</button>
          <div className="nav-divider" />
          <button className={view === 'plans' ? 'active' : ''} onClick={() => { setView('plans'); setMenuOpen(false); }}><Sparkles size={19} /> แพ็กเกจ</button>
          <button className={view === 'settings' ? 'active' : ''} onClick={() => { setView('settings'); setMenuOpen(false); }}><Settings size={19} /> ตั้งค่า</button>
          <button className={view === 'help' ? 'active' : ''} onClick={() => { setView('help'); setMenuOpen(false); }}><CircleHelp size={19} /> ช่วยเหลือ</button>
          <button className={view === 'trash' ? 'active' : ''} onClick={() => { setView('trash');setMenuOpen(false);void refresh(); }}><Trash2 size={19}/> ถังขยะ <span className="nav-count">{trashRecords.length}</span></button>
          {isAdmin && <button className={view === 'admin' ? 'active' : ''} onClick={() => { setView('admin'); setMenuOpen(false); }}><ShieldCheck size={19} /> จัดการระบบ</button>}
        </nav>
        <div className="sidebar-bottom">
          <div className={`sync-card ${cloudActive ? 'on' : ''}`}>
            <div className="sync-icon">{cloudActive ? <Cloud size={19} /> : <CloudOff size={19} />}</div>
            <div><strong>{cloudActive ? 'Cloud Sync เปิดอยู่' : 'เก็บในเครื่อง'}</strong><span>{cloudActive ? 'ข้อมูลพร้อมใช้ทุกอุปกรณ์' : 'ข้อมูลไม่ออกจากอุปกรณ์'}</span></div>
          </div>
          <div className="account-control" ref={accountMenuRef}>
            {accountMenuOpen && <div className="account-menu" role="menu" aria-label="เมนูบัญชี">
              <div className="account-menu-summary"><strong>บัญชีของฉัน</strong><span>{user?.email}</span></div>
              <button role="menuitem" onClick={() => { setView('settings'); setAccountMenuOpen(false); setMenuOpen(false); }}><Settings size={16} /><span>ตั้งค่าบัญชี</span></button>
              <button className="account-menu-signout" role="menuitem" onClick={() => { setAccountMenuOpen(false); void signOut(); }}><LogOut size={16} /><span>ออกจากระบบ</span></button>
            </div>}
            <button className="account-chip" type="button" aria-haspopup="menu" aria-expanded={accountMenuOpen} onClick={() => setAccountMenuOpen((open) => !open)}>
              <div className="avatar">{user?.email?.slice(0, 1).toUpperCase() || 'L'}</div>
              <div><strong>{user?.email || 'Local workspace'}</strong><span>{plan.toUpperCase()} PLAN</span></div>
              <ChevronDown className={accountMenuOpen ? 'open' : ''} size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="icon-btn menu-btn" onClick={() => setMenuOpen(true)}><Menu size={21} /></button>
            <div className="top-search"><Search size={18} /><input aria-label="ค้นหาเอกสาร" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ค้นหาเลขที่เอกสารหรือลูกค้า…" /></div>
          <div className="top-actions">
            <button className="sync-button" onClick={() => void runSync()} disabled={syncing} title={syncError || (plan === 'free' ? 'Cloud Sync สำหรับแพ็กเกจ Pro' : 'ซิงก์ข้อมูล')}><RefreshCw size={17} className={syncing ? 'spin' : ''} /><span>{syncing ? syncStatus : syncError ? 'ซิงก์ไม่สำเร็จ · ลองใหม่' : plan === 'free' ? 'เฉพาะ Pro' : missingFileCount ? 'มีไฟล์แนบสูญหาย' : settings.lastSyncAt ? 'ซิงก์แล้ว' : 'ยังไม่ซิงก์'}</span></button>
            <button className="btn primary" onClick={openNewQuote}><FilePlus2 size={18} /> สร้างใบเสนอราคา</button>
          </div>
        </header>

        <div className="page">
          {user&&cloudExpiry&&['readonly','locked','purged'].includes(cloudExpiry.mode)&&<Suspense fallback={null}><CloudExpiryBanner key={user.id} state={cloudExpiry} userId={user.id} onRenew={()=>setView('plans')}/></Suspense>}
          {user&&plan!=='free'&&<CloudSyncNotifications key={user.id} userId={user.id}/>}
          {syncError && <section className="backup-capacity-warning warning" role="alert"><div className="backup-reminder-icon"><CloudOff size={22} /></div><div><strong>Cloud Sync ยังไม่สำเร็จ</strong><p>{syncError}</p></div><button className="btn ghost small" disabled={syncing} onClick={() => void runSync()}>{syncing ? syncStatus : 'ลองซิงก์ใหม่'}</button></section>}
          {missingFileCount > 0 && <section className="backup-capacity-warning warning" role="alert" aria-label="ไฟล์แนบสูญหาย"><div className="backup-reminder-icon"><CloudOff size={22} /></div><div><strong>ไฟล์แนบสูญหาย {missingFileCount} ไฟล์</strong><p>ข้อมูลเอกสารยังใช้งานและซิงค์ต่อได้ แต่ยังไม่มีต้นฉบับของไฟล์เหล่านี้ ระบบเก็บคีย์อ้างอิงเดิมไว้ กรุณาหาไฟล์จากเครื่องอื่นหรือไฟล์สำรอง แล้วซิงค์ใหม่</p></div><button className="btn ghost small" disabled={syncing} onClick={() => void runSync()}>{syncing ? syncStatus : 'ตรวจไฟล์อีกครั้ง'}</button></section>}
          {(view === 'dashboard' || view === 'quotes') && <>
            <div className="page-heading">
              <div><span className="eyebrow">{view === 'dashboard' ? 'ภาพรวมธุรกิจ' : 'เอกสารทั้งหมด'}</span><h1>{view === 'dashboard' ? 'สวัสดี วันนี้งานเป็นอย่างไรบ้าง' : 'ใบเสนอราคา'}</h1><p>{view === 'dashboard' ? 'ติดตามเอกสารและยอดที่กำลังรอการตอบรับได้ในที่เดียว' : 'สร้าง ค้นหา และจัดการใบเสนอราคาของคุณ'}</p></div>
              <div className="privacy-pill"><ShieldCheck size={17} /><span>Local-first</span><small>ข้อมูลอยู่กับคุณ</small></div>
            </div>

            {view === 'dashboard' && (currentBackupLevel === 'warning' || currentBackupLevel === 'danger') && <section className={`backup-capacity-warning ${currentBackupLevel}`} role={currentBackupLevel === 'danger' ? 'alert' : 'status'}><div className="backup-reminder-icon"><HardDriveDownload size={22} /></div><div><strong>{backupBlocked ? 'ข้อมูลเกินขีดจำกัด Backup' : currentBackupLevel === 'danger' ? 'ข้อมูลใกล้ถึงขีดจำกัด Backup มาก' : 'ข้อมูลใกล้ถึงขีดจำกัด Backup'}</strong><p>ใช้แล้ว {formatStorage(currentBackupSize)} จาก 100 MB ({currentBackupPercent.toLocaleString('th-TH', { maximumFractionDigits: 1 })}%) · {backupBlocked ? 'ไม่สามารถสร้างไฟล์สำรองที่กู้คืนได้ กรุณาลดไฟล์แนบก่อน' : currentBackupLevel === 'danger' ? 'ควรสำรองและลดไฟล์แนบก่อนเพิ่มข้อมูลใหม่' : 'ควรสำรองข้อมูลและตรวจไฟล์แนบที่ไม่จำเป็น'}</p></div><div className="backup-reminder-actions"><button className="btn primary small" disabled={backupBlocked} onClick={() => void exportBackup()}>{backupBlocked ? 'สำรองไม่ได้' : 'สำรองตอนนี้'}</button><button className="btn ghost small" onClick={() => setView('settings')}>จัดการข้อมูล</button></div></section>}

            {backupReminderDue && currentBackupPercent < 85 && <section className="backup-reminder" role="status"><div className="backup-reminder-icon"><HardDriveDownload size={22} /></div><div><strong>อย่าลืมสำรองข้อมูลในเครื่อง</strong><p>แพ็กเกจ Free เก็บข้อมูลไว้ในเบราว์เซอร์เท่านั้น ดาวน์โหลดไฟล์สำรองไว้เพื่อป้องกันข้อมูลสูญหาย</p></div><div className="backup-reminder-actions"><button className="btn primary small" onClick={() => void exportBackup()}>สำรองตอนนี้</button><button className="btn ghost small" onClick={() => void dismissBackupReminder()}>เตือนภายหลัง</button></div></section>}

            {view === 'dashboard' && plan !== 'free' && (receivables.overdueCount > 0 || receivables.dueSoonCount > 0) && <section className={`pro-due-alert ${receivables.overdueCount > 0 ? 'overdue' : ''}`} role="status"><div className="backup-reminder-icon"><FileClock size={22} /></div><div><strong>{receivables.overdueCount > 0 ? `มี ${receivables.overdueCount} ใบแจ้งหนี้เกินกำหนด` : `มี ${receivables.dueSoonCount} ใบแจ้งหนี้ใกล้ครบกำหนด`}</strong><p>{receivables.overdueCount > 0 ? `ยอดที่เกินกำหนด ${money(receivables.overdue)} และยอดครบกำหนดใน 7 วัน ${money(receivables.dueSoon)}` : `ยอดที่ควรติดตามใน 7 วัน ${money(receivables.dueSoon)}`}</p></div><button className="btn primary small" onClick={() => setView('reports')}>ดูรายงานลูกหนี้</button></section>}

            {view === 'dashboard' && <section className="metric-grid">
              <article className="metric-card green"><div className="metric-icon"><CircleDollarSign size={21} /></div><span>มูลค่าใบเสนอราคาทั้งหมด</span><strong>{money(metrics.total)}</strong><small>{quotes.length} เอกสาร</small></article>
              <article className="metric-card dark"><div className="metric-icon"><FileCheck2 size={21} /></div><span>รับชำระแล้ว</span><strong>{money(metrics.received)}</strong><small>รวมทุกงวดที่ยังไม่ยกเลิก</small></article>
              <article className="metric-card cream"><div className="metric-icon"><FileClock size={21} /></div><span>ยอดคงเหลือ</span><strong>{money(metrics.outstanding)}</strong><small>จากใบแจ้งหนี้ทั้งหมด</small></article>
              <article className="metric-card lilac"><div className="metric-icon"><ReceiptText size={21} /></div><span>ชำระครบ</span><strong>{metrics.paidInvoices}</strong><small>จาก {invoices.length} ใบแจ้งหนี้</small></article>
            </section>}

            <section className="content-card">
              <div className="card-toolbar"><div><h2>{view === 'dashboard' ? 'เอกสารล่าสุด' : 'รายการใบเสนอราคา'}</h2><span>{filteredQuotes.length} รายการ</span></div><div className="filters"><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as QuoteStatus | 'all')}><option value="all">ทุกสถานะ</option>{Object.entries(statusMeta).map(([value, meta]) => <option value={value} key={value}>{meta.label}</option>)}</select><button className="btn ghost small" disabled={backupBlocked} title={backupBlocked ? 'ข้อมูลเกิน 100 MB กรุณาลดไฟล์แนบก่อน' : 'สำรองข้อมูล'} onClick={() => void exportBackup()}><ArrowDownToLine size={15} /> {backupBlocked ? 'สำรองไม่ได้' : 'สำรองข้อมูล'}</button></div></div>
              {filteredQuotes.length === 0 ? <div className="empty-state"><div><FilePlus2 size={28} /></div><h3>เริ่มจากใบเสนอราคาใบแรก</h3><p>สร้างได้ทันทีโดยไม่ต้องสมัคร ข้อมูลจะถูกเก็บในอุปกรณ์นี้</p><button className="btn primary" onClick={openNewQuote}>สร้างใบเสนอราคา</button></div> :
              <div className="quote-list">
                <div className="quote-list-head"><span>เอกสาร</span><span>ลูกค้า</span><span>วันที่</span><span>ยอดสุทธิ</span><span>สถานะ</span><span /></div>
                {filteredQuotes.slice(0, view === 'dashboard' ? 6 : undefined).map((quote) => <div className="quote-row" key={quote.id} onDoubleClick={() => setPreviewQuote(quote)}>
                  <div className="quote-primary"><button className="doc-number" onClick={() => setPreviewQuote(quote)}>{quote.number}</button><small>{quote.acceptance ? `ยอมรับโดย ${quote.acceptance.acceptedBy} · ${shortDate(quote.acceptance.acceptedAt)}` : `${quote.items.length} รายการ · ${quote.syncState === 'synced' ? 'บนคลาวด์' : 'ในเครื่อง'}`}</small></div>
                  <strong className="quote-customer">{quote.customerName || '—'}</strong>
                  <span className="quote-date">{shortDate(quote.issueDate)}</span>
                  <strong className="quote-total">{money(quoteTotal(quote))}</strong>
                  <div className="status-select-wrap"><span className={`status-dot ${statusMeta[quote.status].className}`} /><select value={quote.status} disabled={quote.status === 'accepted' || quote.status === 'replaced'} onChange={(e) => void updateStatus(quote, e.target.value as QuoteStatus)}>{Object.entries(statusMeta).filter(([value]) => value !== 'replaced' || quote.status === 'replaced').map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select><ChevronDown size={14} /></div>
                  <dl className="quote-mobile-details"><div><dt>ลูกค้า</dt><dd>{quote.customerName || '—'}</dd></div><div><dt>วันที่ออก</dt><dd>{shortDate(quote.issueDate)}</dd></div><div><dt>ยอดสุทธิ</dt><dd>{money(quoteTotal(quote))}</dd></div></dl>
                  <div className="row-menu">{quote.acceptance && <button className="icon-btn" onClick={() => setAcceptanceTarget({ quote, action: 'view' })} title="ดูการยอมรับ"><FileSignature size={16} /></button>}<button className="icon-btn" onClick={() => void openOrCreateInvoice(quote)} disabled={quote.status === 'replaced'} title={quote.status === 'replaced' ? 'ใช้ฉบับแก้ไขล่าสุดแทน' : invoices.some((invoice) => invoice.quoteId === quote.id) ? 'เปิดใบแจ้งหนี้' : 'สร้างใบแจ้งหนี้'}><FileOutput size={16} /></button><button className="icon-btn" onClick={() => openQuoteCopy(quote)} title="ทำสำเนา"><Copy size={16} /></button><button className="icon-btn" onClick={() => openEditQuote(quote)} title={quote.status === 'accepted' ? 'สร้างฉบับแก้ไข' : quote.status === 'replaced' ? 'เปิดฉบับแก้ไขล่าสุด' : 'แก้ไข'}><Pencil size={16} /></button><button className="icon-btn danger" onClick={() => void handleDelete(quote)} title="ลบ"><Trash2 size={16} /></button></div>
                </div>)}
              </div>}
            </section>
          </>}

          {view === 'invoices' && <>
            <div className="page-heading">
              <div><span className="eyebrow">บันทึกรับชำระ</span><h1>ใบแจ้งหนี้และการรับเงิน</h1><p>คุณรับเงินผ่านช่องทางของคุณ แล้วบันทึกยอดที่ได้รับจริงหลายงวด แนบหลักฐาน และออกใบเสร็จได้จากอุปกรณ์นี้ — BilaFlow ไม่ได้รับเงินแทนคุณ</p></div>
              <div className="privacy-pill"><ShieldCheck size={17} /><span>Local-only</span><small>สลิปไม่ออกจากอุปกรณ์</small></div>
            </div>
            <section className="invoice-summary-strip">
              <div><span>ใบแจ้งหนี้</span><strong>{invoices.length}</strong></div>
              <div><span>รับชำระแล้ว</span><strong>{money(metrics.received)}</strong></div>
              <div><span>ยอดคงเหลือ</span><strong>{money(metrics.outstanding)}</strong></div>
            </section>
            <section className="content-card">
              <div className="card-toolbar"><div><h2>รายการใบแจ้งหนี้</h2><span>{filteredInvoices.length} รายการ</span></div><button className="btn ghost small" onClick={() => setView('quotes')}><FilePlus2 size={15} /> เลือกใบเสนอราคาเพื่อสร้าง</button></div>
              {filteredInvoices.length === 0 ? <div className="empty-state"><div><ReceiptText size={28} /></div><h3>ยังไม่มีใบแจ้งหนี้</h3><p>เปิดรายการใบเสนอราคา แล้วกดไอคอนสร้างใบแจ้งหนี้ในแถวที่ต้องการ</p><button className="btn primary" onClick={() => setView('quotes')}>ไปที่ใบเสนอราคา</button></div> : <div className="invoice-list">
                <div className="invoice-list-head"><span>ใบแจ้งหนี้</span><span>ลูกค้า</span><span>ครบกำหนด</span><span>ยอดรวม</span><span>รับแล้ว</span><span>คงเหลือ</span><span>สถานะ</span></div>
                {filteredInvoices.map((invoice) => {
                  const paid = invoicePaidAmount(invoice.id, payments);
                  const balance = invoiceBalance(invoice, payments);
                  const status = invoiceStatus(invoice, payments);
                  return <button className="invoice-row" key={invoice.id} onClick={() => setManagerInvoice(invoice)}>
                    <div><strong>{invoice.number}</strong><small>อ้างอิง {invoice.quoteSnapshot.number}</small></div><strong>{invoice.quoteSnapshot.customerName}</strong><span>{shortDate(invoice.dueDate)}</span><span>{money(quoteTotal(invoice.quoteSnapshot))}</span><span className="paid-text">{money(paid)}</span><strong>{money(balance)}</strong><span className={`invoice-status ${invoiceStatusMeta[status].className}`}>{invoiceStatusMeta[status].label}</span>
                  </button>;
                })}
              </div>}
            </section>
          </>}

          {view === 'settings' && <SettingsView settings={settings} currentBackupSize={currentBackupSize} backupBlocked={backupBlocked} setSettings={setSettings} onSave={saveCompany} onToggleCloud={() => void toggleCloud()} onToggleUsage={() => void toggleUsageAnalytics()} onUpgrade={() => setView('plans')} syncing={syncing} user={user} plan={plan} onSignOut={() => void signOut()} onExport={() => void exportBackup()} onImport={() => importRef.current?.click()} onDeleteAccount={deleteAccount} onNotice={flash} configured={isSupabaseConfigured} />}
          {view === 'reports' && <ProReceivablesView invoices={invoices} payments={payments} plan={plan} onOpenInvoice={setManagerInvoice} onUpgrade={() => setView('plans')} />}
          {view === 'help' && <HelpView lastBackupAt={settings.lastBackupAt} backupBlocked={backupBlocked} onBackup={() => void exportBackup()} onCreateQuote={openNewQuote} />}
          {view === 'trash' && <DocumentTrashView records={trashRecords} busy={syncing||trashWorking} onRestore={record=>{void performTrashAction(record,'restore');}} onRefresh={()=>{if(cloudActive)void runSync();else void refresh();}}/>}
          {view === 'plans' && <PlansView key={user?.id || 'local'} userId={user?.id || ''} currentPlan={plan} planExpiresAt={profile?.planExpiresAt ?? null} onSubscriptionChange={flash} />}
          {view === 'admin' && isAdmin && <AdminView key={user.id} currentUserId={user.id} />}
        </div>
      </main>

      {editorQuote !== undefined && <QuoteEditor initial={editorQuote} mode={editorMode} customers={customers} company={settings.company} plan={plan} existingQuotes={quotes} nextNumber={nextNumber} onClose={() => setEditorQuote(undefined)} onSave={handleSaveQuote} />}
      {previewQuote && <QuotePreview quote={previewQuote} company={settings.company} plan={plan} onClose={() => setPreviewQuote(null)} />}
      {managerInvoice && <InvoiceManager invoice={managerInvoice} payments={payments} company={settings.company} plan={plan} onClose={() => setManagerInvoice(null)} onAddPayment={(draft) => addPayment(managerInvoice, draft)} onVoidPayment={(payment) => voidPayment(managerInvoice, payment)} onArchive={() => archiveInvoice(managerInvoice)} onUpdateDueDate={(dueDate) => updateInvoiceDueDate(managerInvoice, dueDate)} />}
      {acceptanceTarget && <QuoteAcceptanceModal quote={acceptanceTarget.quote} readOnly={acceptanceTarget.action === 'view'} onClose={() => setAcceptanceTarget(null)} onAccept={recordAcceptance} />}
      {notice && <div className="toast"><Check size={17} />{notice}</div>}
      <input ref={importRef} type="file" accept="application/json" hidden onChange={importBackup} />
      {menuOpen && <button className="sidebar-scrim" aria-label="ปิดเมนู" onClick={() => setMenuOpen(false)} />}
    </div>
  );
}

interface SettingsViewProps {
  settings: AppSettings;
  currentBackupSize: number;
  backupBlocked: boolean;
  setSettings: (value: AppSettings) => void;
  onSave: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onToggleCloud: () => void;
  onToggleUsage: () => void;
  onUpgrade: () => void;
  syncing: boolean;
  user: User | null;
  plan: Plan;
  onSignOut: () => void;
  onExport: () => void;
  onImport: () => void;
  onDeleteAccount: (reasonCategory: string, reasonDetails: string, confirmationEmail: string) => Promise<void>;
  onNotice: (message: string) => void;
  configured: boolean;
}

function SettingsView(props: SettingsViewProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('no_longer_needed');
  const [deleteDetails, setDeleteDetails] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const company = props.settings.company;
  const logo = useCloudFile(company.logoDataUrl);
  const companyField = (key: keyof typeof company, value: string) => props.setSettings({ ...props.settings, company: { ...company, [key]: value } });
  const handleLogo = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      window.alert('กรุณาใช้ไฟล์โลโก้ขนาดไม่เกิน 1 MB');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => companyField('logoDataUrl', String(reader.result || ''));
    reader.readAsDataURL(file);
    event.target.value = '';
  };
  const brandLabel = company.brandName?.trim() || company.name;
  const brandInitials = brandLabel.split(/\s+/).filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'BF';
  const currentBackupSize = props.currentBackupSize;
  const backupRemaining = Math.max(0, MAX_BACKUP_BYTES - currentBackupSize);
  const backupPercent = Math.min(100, (currentBackupSize / MAX_BACKUP_BYTES) * 100);
  const backupLevel = backupUsageLevel(currentBackupSize);
  const submitDelete = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDeleteError('');
    setDeleting(true);
    try {
      await props.onDeleteAccount(deleteReason, deleteDetails, deleteConfirmation);
    } catch (reason) {
      setDeleteError(reason instanceof Error ? reason.message : 'ลบบัญชีไม่สำเร็จ');
      setDeleting(false);
    }
  };
  return <>
    <div className="page-heading"><div><span className="eyebrow">ตั้งค่าระบบ</span><h1>ข้อมูลของคุณ คุณเป็นคนเลือกที่เก็บ</h1><p>ใช้งานในเครื่องได้ทันที หรือเปิด Cloud Sync เพื่อใช้ข้อมูลชุดเดียวกันหลายอุปกรณ์</p></div></div>
    <div className="settings-grid">
      <section className="content-card settings-card"><div className="card-heading"><div className="setting-icon"><Settings size={20} /></div><div><h2>ข้อมูลผู้ขาย / กิจการ</h2><p>ใช้ฟอร์มรายละเอียดชุดเดียวกับฝั่งลูกค้า และแสดงบนใบเสนอราคา</p></div></div><form className="settings-form" onSubmit={props.onSave}>
        <div className="brand-customizer">
          <div className="brand-preview-mark">{logo.url ? <img src={logo.url} alt="ตัวอย่างโลโก้" /> : <span>{brandInitials}</span>}</div>
          <div className="brand-fields">
            <label><span>ชื่อแบรนด์บนหัวเอกสาร</span><input value={company.brandName ?? ''} onChange={(e) => companyField('brandName', e.target.value)} placeholder={company.name} /></label>
            <label><span>โลโก้</span><input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogo} /></label>
            <small>PNG, JPG, WebP หรือ SVG ไม่เกิน 1 MB · คำว่า QUOTATION จะคงเดิม</small>
            {company.logoDataUrl && <button className="text-button" type="button" onClick={() => companyField('logoDataUrl', '')}>นำโลโก้ออก</button>}
            {isMissingCloudFile(company.logoDataUrl) && <small role="status">ไฟล์โลโก้สูญหาย กรุณาเลือกต้นฉบับใหม่ หรือซิงค์จากเครื่องที่ยังมีไฟล์</small>}
          </div>
        </div>
        <div className="field-grid two">
          <label><span>ประเภทผู้ขาย</span><select value={company.type} onChange={(e) => companyField('type', e.target.value)}><option value="company">นิติบุคคล / บริษัท</option><option value="individual">บุคคลธรรมดา</option></select></label>
          <label><span>{company.type === 'company' ? 'ชื่อบริษัท / ร้านค้า' : 'ชื่อ–นามสกุล'}</span><input value={company.name} onChange={(e) => companyField('name', e.target.value)} required /></label>
        </div>
        <div className="field-grid two">
          <label><span>เลขประจำตัวผู้เสียภาษี</span><input inputMode="numeric" pattern="[0-9]{13}" title="กรอกเลขประจำตัวผู้เสียภาษี 13 หลัก" value={company.taxId} onChange={(e) => companyField('taxId', e.target.value.replace(/\D/g, '').slice(0, 13))} /></label>
          {company.type === 'company' && <label><span>สำนักงาน</span><select value={company.branchType} onChange={(e) => companyField('branchType', e.target.value)}><option value="headOffice">สำนักงานใหญ่</option><option value="branch">สาขา</option></select></label>}
        </div>
        {company.type === 'company' && <label><span>รหัสสาขา</span><input inputMode="numeric" pattern="[0-9]{5}" title="กรอกรหัสสาขา 5 หลัก" value={company.branchCode} onChange={(e) => companyField('branchCode', e.target.value.replace(/\D/g, '').slice(0, 5))} required={company.branchType === 'branch'} disabled={company.branchType !== 'branch'} placeholder={company.branchType === 'branch' ? 'เช่น 00001' : 'ไม่ต้องกรอกสำหรับสำนักงานใหญ่'} /></label>}
        <div className="field-grid two"><label><span>ชื่อผู้ติดต่อ</span><input value={company.contactName} onChange={(e) => companyField('contactName', e.target.value)} /></label><label><span>เบอร์โทรศัพท์</span><input type="tel" value={company.phone} onChange={(e) => companyField('phone', e.target.value)} /></label></div>
        <label><span>อีเมล</span><input type="email" value={company.email} onChange={(e) => companyField('email', e.target.value)} /></label>
        <label><span id="company-address-label">ที่อยู่สำหรับออกเอกสาร</span><textarea rows={3} aria-labelledby="company-address-label" aria-describedby="company-address-hint" value={company.address} onChange={(e) => companyField('address', e.target.value)} placeholder="บ้านเลขที่ ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์" /><small id="company-address-hint" className="field-hint">ขึ้นบรรทัดใหม่ได้ · ชื่อ เลขผู้เสียภาษี โทรศัพท์ และอีเมล ให้กรอกในช่องแยกด้านบน</small></label>
        <div className="field-grid two"><label><span>ธนาคาร</span><input value={company.bankName} onChange={(e) => companyField('bankName', e.target.value)} /></label><label><span>เลขบัญชี</span><input value={company.bankAccount} onChange={(e) => companyField('bankAccount', e.target.value)} /></label></div>
        <button className="btn primary" type="submit">บันทึกข้อมูลผู้ขาย</button>
      </form></section>
      <div className="settings-stack">
        {props.user && <AccountSecurity user={props.user} onNotice={props.onNotice} />}
        <section className="content-card settings-card cloud-panel"><div className="card-heading"><div className="setting-icon cloud"><Cloud size={20} /></div><div><h2>บัญชีและ Cloud Sync</h2><p>{props.plan === 'free' ? 'บัญชี Free เก็บเอกสารแยกไว้ในอุปกรณ์นี้' : props.configured ? 'ใช้ข้อมูลชุดเดียวกันได้ในทุกอุปกรณ์' : 'บริการนี้ยังไม่เปิดใช้งาน'}</p></div></div><div className="signed-in"><div className="avatar large">{props.user?.email?.slice(0, 1).toUpperCase()}</div><div><strong>{props.user?.email}</strong><span>แพ็กเกจ {props.plan.toUpperCase()}</span></div><button className="icon-btn" onClick={props.onSignOut} title="ออกจากระบบ"><LogOut size={17} /></button></div>{props.plan === 'free' ? <div className="setup-message upgrade-message"><Sparkles size={22} /><div><strong>ข้อมูล Free จะเก็บในอุปกรณ์นี้เท่านั้น</strong><p>บัญชีช่วยแยกข้อมูลของผู้ใช้แต่ละคน แต่ยังไม่ซิงก์ข้ามอุปกรณ์</p><button className="btn ghost small" type="button" onClick={props.onUpgrade}>ดูแพ็กเกจ Pro</button></div></div> : !props.configured ? <div className="setup-message"><ShieldCheck size={22} /><div><strong>ข้อมูลของคุณยังเก็บอยู่ในอุปกรณ์นี้</strong><p>เมื่อ Cloud Sync พร้อมใช้งาน ระบบจะแจ้งให้ทราบในหน้านี้</p></div></div> : <button className={`cloud-toggle ${props.settings.cloudSyncEnabled ? 'enabled' : ''}`} onClick={props.onToggleCloud} disabled={props.syncing}><div><strong>{props.settings.cloudSyncEnabled ? 'เปิด Cloud Sync อยู่' : 'Cloud Sync ปิดอยู่'}</strong><span>{props.settings.cloudSyncEnabled ? 'ข้อมูลจะซิงก์อัตโนมัติหลังแก้ไข' : 'ข้อมูลยังอยู่ในอุปกรณ์นี้เท่านั้น'}</span></div><span className="switch"><i /></span></button>}</section>
        <section className="content-card settings-card"><div className="card-heading"><div className="setting-icon"><FileClock size={20} /></div><div><h2>สถิติการใช้งาน</h2><p>ช่วยวิเคราะห์และพัฒนา BilaFlow โดยไม่ส่งเนื้อหาเอกสาร</p></div></div><button type="button" className={`cloud-toggle ${props.settings.usageAnalyticsEnabled ? 'enabled' : ''}`} onClick={props.onToggleUsage}><div><strong>{props.settings.usageAnalyticsEnabled ? 'แชร์สถิติแบบไม่เปิดเผยเนื้อหา' : 'ไม่แชร์สถิติการใช้งาน'}</strong><span>{props.settings.usageAnalyticsEnabled ? 'ส่งเฉพาะจำนวน QT, Invoice, Receipt และวันที่ใช้งานล่าสุด' : 'Admin จะเห็นว่าไม่ได้แชร์สถิติ ไม่ใช่เอกสาร 0 รายการ'}</span></div><span className="switch"><i /></span></button><div className="usage-privacy-note"><ShieldCheck size={15} /><span>ไม่ส่งเลขที่เอกสาร ชื่อลูกค้า ยอดเงิน รายการสินค้า หมายเหตุ หรือไฟล์แนบ</span></div></section>
        {props.plan !== 'free' && props.user && props.configured && <CloudStorageCard key={props.user.id} userId={props.user.id} refreshKey={`${props.settings.lastSyncAt}:${props.syncing}`} />}
        {props.plan === 'free' && props.user && props.configured && <StorageRenewalManager key={props.user.id} userId={props.user.id} />}
        <section className="content-card settings-card"><div className="card-heading"><div className="setting-icon"><HardDriveDownload size={20} /></div><div><h2>ไฟล์สำรองในเครื่อง</h2><p>ขนาดเอกสารและไฟล์แนบที่จะดาวน์โหลดเป็นไฟล์สำรอง แยกจากโควตา Cloud</p></div></div>
          <div className={`storage-estimate ${backupLevel}`}>
            <div className="storage-estimate-summary"><div><span>พื้นที่ที่เหลือสำหรับส่งออก JSON หนึ่งไฟล์โดยประมาณ</span><strong>{formatStorage(backupRemaining)}</strong></div></div>
            <div className="storage-meter" role="progressbar" aria-label="ขนาดข้อมูลสำรองที่ใช้แล้ว" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(backupPercent)}><i style={{ width: `${Math.max(backupPercent, currentBackupSize > 0 ? 1 : 0)}%` }} /></div>
            <div className="storage-estimate-details"><span>ข้อมูลในเครื่อง {formatStorage(currentBackupSize)} ({backupPercent.toLocaleString('th-TH', { maximumFractionDigits: 1 })}%)</span><span>ขีดจำกัดส่งออก JSON 100 MB ไม่ใช่พื้นที่ Local</span></div>
            <p>Pro มี Cloud 5 GB ต่อบัญชี เครื่องอื่นจะอ่านทะเบียนเอกสารและโหลดไฟล์เมื่อเปิดใช้ ไม่ต้องดาวน์โหลดทั้งหมด ตัวเลขข้างบนยังไม่รวมไฟล์ Cloud ที่ยังไม่ได้โหลด การส่งออกจะตรวจขนาดจริงอีกครั้ง</p>
            <p>{props.backupBlocked ? 'ข้อมูลเกิน 100 MB จึงไม่สามารถสร้างไฟล์สำรองที่กู้คืนได้ กรุณาลดไฟล์แนบก่อน' : backupPercent >= 95 ? 'ใกล้ถึงขีดจำกัดมาก กรุณาสำรองข้อมูลและลดไฟล์แนบก่อนเพิ่มข้อมูลใหม่' : backupPercent >= 85 ? 'ใกล้ถึงขีดจำกัด ควรสำรองข้อมูลและตรวจไฟล์แนบที่ไม่จำเป็น' : backupPercent >= 70 ? 'ข้อมูลเริ่มมีขนาดใหญ่ แนะนำให้สำรองข้อมูลเป็นประจำ' : 'รวมเอกสาร PDF/รูปหลักฐาน สลิป โลโก้ และการตั้งค่าที่จะอยู่ในไฟล์สำรอง'}</p>
          </div>
        </section>
        <section className="content-card settings-card"><div className="card-heading"><div className="setting-icon"><ArrowDownToLine size={20} /></div><div><h2>สำรองข้อมูลด้วยตัวเอง</h2><p>รวมเอกสาร หลักฐาน PDF/รูป และสลิปไว้ในไฟล์ JSON</p></div></div><div className={`backup-scope-note ${props.backupBlocked ? 'blocked' : ''}`}><FileCheck2 size={16} /><span>{props.backupBlocked ? 'ข้อมูลเกิน 100 MB ไม่สามารถส่งออกไฟล์ที่รองรับการกู้คืนได้ กรุณาลดไฟล์แนบก่อน' : 'เมื่อนำเข้าในคอมเครื่องอื่น ไฟล์หลักฐานที่ผูกกับ QT และรายการรับเงินจะกลับมาด้วย'}</span></div><div className="backup-actions"><button className="btn ghost" disabled={props.backupBlocked} onClick={props.onExport}><ArrowDownToLine size={16} /> {props.backupBlocked ? 'สำรองไม่ได้' : 'ส่งออกข้อมูล'}</button><button className="btn ghost" onClick={props.onImport}><ArrowUpFromLine size={16} /> นำเข้าข้อมูล</button></div></section>
        <section className="content-card settings-card danger-zone"><div className="card-heading"><div className="setting-icon danger"><Trash2 size={20} /></div><div><h2>ลบบัญชี</h2><p>ลบบัญชีและข้อมูลส่วนกลางอย่างถาวร</p></div></div><p>ควรส่งออกไฟล์สำรองก่อนดำเนินการ ข้อมูล Free ในอุปกรณ์นี้จะถูกลบ แต่ข้อมูลที่ยังอยู่ในเบราว์เซอร์ของอุปกรณ์อื่นต้องลบจากอุปกรณ์นั้นแยกต่างหาก</p><button className="btn danger-button" type="button" onClick={() => setDeleteOpen(true)}><Trash2 size={16} /> ลบบัญชีของฉัน</button></section>
      </div>
    </div>
    {deleteOpen && <div className="account-delete-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !deleting) setDeleteOpen(false); }}><form className="account-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-account-title" onSubmit={submitDelete}><header><div><span className="eyebrow">Permanent deletion</span><h2 id="delete-account-title">ลบบัญชี BilaFlow</h2></div><button className="icon-btn" type="button" aria-label="ปิด" disabled={deleting} onClick={() => setDeleteOpen(false)}><X size={18} /></button></header><div className="account-delete-warning"><Trash2 size={20} /><div><strong>การดำเนินการนี้ย้อนกลับไม่ได้</strong><p>ระบบจะลบบัญชี ข้อมูลแพ็กเกจ สถิติ และข้อมูล Cloud/R2 ที่เป็นของบัญชีนี้ พร้อมล้างข้อมูล Free ในอุปกรณ์ปัจจุบัน</p></div></div><label><span>เหตุผลหลัก</span><select value={deleteReason} onChange={(event) => setDeleteReason(event.target.value)}><option value="no_longer_needed">ไม่ต้องการใช้งานแล้ว</option><option value="missing_features">ฟีเจอร์ไม่ตรงกับความต้องการ</option><option value="privacy">กังวลเรื่องความเป็นส่วนตัว</option><option value="duplicate_account">มีบัญชีซ้ำ</option><option value="other">เหตุผลอื่น</option></select></label><label><span>รายละเอียดเหตุผล</span><textarea rows={4} minLength={5} maxLength={1000} required value={deleteDetails} onChange={(event) => setDeleteDetails(event.target.value)} placeholder="ช่วยบอกเหตุผลอย่างน้อย 5 ตัวอักษร เพื่อให้เราปรับปรุงบริการ" /></label><label><span>พิมพ์อีเมลบัญชีเพื่อยืนยัน</span><input type="email" autoComplete="off" required value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} placeholder={props.user?.email ?? ''} /></label><div className="account-delete-local-note"><strong>ก่อนลบ:</strong> กด “ส่งออกข้อมูล” ในหน้าตั้งค่าหากต้องการเก็บเอกสารไว้ อุปกรณ์อื่นไม่สามารถถูกล้างจากระยะไกลได้</div>{deleteError && <div className="form-error account-delete-error">{deleteError}</div>}<footer><button className="btn ghost" type="button" disabled={deleting} onClick={() => setDeleteOpen(false)}>ยกเลิก</button><button className="btn danger-button" type="submit" disabled={deleting || deleteDetails.trim().length < 5 || deleteConfirmation.trim().toLocaleLowerCase() !== (props.user?.email ?? '').trim().toLocaleLowerCase()}>{deleting ? <><RefreshCw className="spin" size={16} /> กำลังลบ…</> : <><Trash2 size={16} /> ยืนยันลบบัญชีถาวร</>}</button></footer></form></div>}
  </>;
}

function HelpView({ lastBackupAt, backupBlocked, onBackup, onCreateQuote }: { lastBackupAt: string | null; backupBlocked: boolean; onBackup: () => void; onCreateQuote: () => void }) {
  const backupLabel = lastBackupAt
    ? new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(lastBackupAt))
    : 'ยังไม่เคยสำรองข้อมูล';
  return <>
    <div className="page-heading"><div><span className="eyebrow">คู่มือแพ็กเกจ Free</span><h1>เริ่มใช้งานและดูแลข้อมูลของคุณ</h1><p>ตั้งแต่ใบเสนอราคาไปจนถึงรับชำระและออกใบเสร็จ</p></div><div className="privacy-pill"><ShieldCheck size={17} /><span>Local-only</span><small>ไม่ส่งข้อมูลขึ้นเซิร์ฟเวอร์</small></div></div>
    <div className="help-grid">
      <section className="content-card help-card"><span className="help-number">01</span><h2>เริ่มสร้างเอกสาร</h2><ol><li>กรอกข้อมูลผู้ขายในหน้า “ตั้งค่า”</li><li>กด “สร้างใบเสนอราคา” และกรอกข้อมูลลูกค้า</li><li>เพิ่มรายการ จำนวน หน่วย ราคา ภาษี และเงื่อนไข</li><li>บันทึก ตรวจตัวอย่าง แล้วดาวน์โหลด PDF</li></ol><button className="btn primary" onClick={onCreateQuote}><FilePlus2 size={17} /> สร้างใบเสนอราคา</button></section>
      <section className="content-card help-card"><span className="help-number">02</span><h2>ยืนยันและรับชำระ</h2><ol><li>ส่ง PDF ที่มีช่องลงนามให้ลูกค้า</li><li>บันทึกชื่อ วันที่ ช่องทาง และหลักฐานเมื่อลูกค้ายอมรับ</li><li>ระบบล็อกฉบับที่ยอมรับแล้วก่อนสร้างใบแจ้งหนี้</li><li>เพิ่มยอดรับชำระทีละงวดและออกใบเสร็จ PDF</li></ol><p className="help-warning">หากต้องแก้ใบเสนอราคาที่ลูกค้ายอมรับแล้ว ระบบจะสร้างฉบับแก้ไขใหม่และต้องยอมรับอีกครั้ง</p></section>
      <section className="content-card help-card"><span className="help-number">03</span><h2>ข้อมูลเก็บไว้ที่ไหน</h2><p>แพ็กเกจ Free เก็บใบเสนอราคา ใบแจ้งหนี้ รายการรับเงิน สลิป โลโก้ และการตั้งค่าใน IndexedDB ของเบราว์เซอร์บนอุปกรณ์นี้</p><p className="help-warning">การล้างข้อมูลเว็บไซต์ เปลี่ยนเบราว์เซอร์ หรือลงระบบใหม่ อาจทำให้ข้อมูลในเครื่องหายได้</p></section>
      <section className="content-card help-card"><span className="help-number">04</span><h2>สำรองและกู้คืน</h2><p>{backupBlocked ? 'ข้อมูลปัจจุบันเกิน 100 MB จึงยังไม่สามารถสร้างไฟล์สำรองที่รองรับการกู้คืนได้ กรุณาลดไฟล์แนบจากหน้าจัดการข้อมูลก่อน' : 'ไฟล์ JSON จะรวมเอกสาร รายการรับเงิน และหลักฐานที่แนบทั้งหมด ควรเก็บไว้ในพื้นที่ปลอดภัย ระบบจะสำรองข้อมูลเดิมก่อนนำเข้าไฟล์ใหม่เสมอ'}</p><div className="backup-status"><HardDriveDownload size={17} /><div><span>สำรองล่าสุด</span><strong>{backupLabel}</strong></div></div><button className="btn ghost" disabled={backupBlocked} onClick={onBackup}><ArrowDownToLine size={16} /> {backupBlocked ? 'สำรองไม่ได้' : 'ดาวน์โหลดไฟล์สำรอง'}</button></section>
      <section className="content-card help-card"><span className="help-number">05</span><h2>ความเป็นส่วนตัว</h2><ul><li>PDF และไฟล์สำรองถูกสร้างภายในอุปกรณ์</li><li>แพ็กเกจ Free ไม่อัปโหลดเนื้อหาเอกสารหรือสลิปขึ้นเซิร์ฟเวอร์โดยอัตโนมัติ</li><li>ระบบจะแจ้งก่อนส่งสถิติจำนวนรวมครั้งแรก โดยเปิดไว้เป็นค่าเริ่มต้นและปิดได้ทุกเมื่อ ผู้ดูแลไม่เห็นเนื้อหาเอกสาร Free ที่อยู่ในอุปกรณ์</li><li>ควรปกป้องไฟล์สำรองเพราะมีข้อมูลลูกค้าและหลักฐานการเงิน</li><li>Cloud Sync จะเปิดเฉพาะเมื่ออัปเกรดและผู้ใช้เลือกเปิดเอง</li></ul></section>
    </div>
    <div className="admin-privacy-note help-privacy-note"><ShieldCheck size={19} /><div><strong>คุณเป็นผู้ควบคุมข้อมูล</strong><p>ข้อมูลลูกค้าในใบเสนอราคาเป็นความรับผิดชอบของผู้ใช้งาน โปรดแจ้งวัตถุประสงค์การใช้งาน เก็บเท่าที่จำเป็น และลบเมื่อหมดความจำเป็นตามกฎหมายที่เกี่ยวข้อง</p></div></div>
  </>;
}

type AdminData = {
  summary: { total: number; cloudSync: number; paid: number; suspended: number; reportedDocuments: number; sharingAccounts: number };
  observability: { errors24h: number; fatal24h: number; bounces24h: number; complaints24h: number };
  pagination: { page: number; pageSize: number; totalPages: number; filtered: number };
  users: Array<{
    id: string;
    email: string;
    displayName: string;
    plan: Plan;
    role: string;
    status: 'active' | 'suspended';
    cloudSync: boolean;
    createdAt: string;
    lastSignInAt: string | null;
    planExpiresAt: string | null;
    emailConfirmedAt: string | null;
    authProvider: string;
    usage: {
      sharingEnabled: boolean;
      quotes: number;
      invoices: number;
      receipts: number;
      recent30: number;
      lastActivityAt: string | null;
      appVersion: string;
      reportedAt: string | null;
    } | null;
  }>;
  audit: Array<{
    id: number;
    action: string;
    adminEmail: string;
    targetEmail: string;
    details: { plan?: Plan; accountStatus?: 'active' | 'suspended'; planExpiresAt?: string | null };
    createdAt: string;
  }>;
};

const auditDescription = (details: AdminData['audit'][number]['details']) => {
  const changes: string[] = [];
  if (details.plan) changes.push(`แพ็กเกจเป็น ${details.plan.toUpperCase()}`);
  if (details.accountStatus) changes.push(details.accountStatus === 'active' ? 'เปิดใช้งานบัญชี' : 'ระงับบัญชี');
  if ('planExpiresAt' in details) changes.push(details.planExpiresAt ? `วันหมดอายุ ${shortDate(details.planExpiresAt)}` : 'ล้างวันหมดอายุ');
  return changes.join(' · ') || 'ปรับปรุงข้อมูลบัญชี';
};

function AdminView({ currentUserId }: { currentUserId: string }) {
  const [adminSection,setAdminSection]=useState<'system'|'accounts'>('system');
  const [adminAuthorized,setAdminAuthorized]=useState(false);
  const [data, setData] = useState<AdminData | null>(null);
  const [error, setError] = useState('');
  const [mfaNotice, setMfaNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [mfaChecked, setMfaChecked] = useState(false);
  const [factorId, setFactorId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [enrollment, setEnrollment] = useState<{ qr: string; secret: string } | null>(null);
  const [savingUserId, setSavingUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState<'all' | Plan>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [pageSize, setPageSize] = useState<25 | 50>(25);
  const [selectedUserId, setSelectedUserId] = useState('');
  const adminRequestRef = useRef(0);
  const selectedUser = data?.users.find((row) => row.id === selectedUserId) ?? null;

  useEffect(() => {
    if (!selectedUserId) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedUserId('');
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedUserId]);

  const clearPendingAdminFactors = async () => {
    if (!supabase) return false;
    const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
    if (factorsError) throw factorsError;
    const pending = factors?.all.filter((factor) => factor.factor_type === 'totp' && factor.status === 'unverified' && ['QuoteFlow Admin', 'BilaFlow Admin'].includes(factor.friendly_name ?? '')) ?? [];
    for (const factor of pending) {
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
      if (unenrollError) throw unenrollError;
    }
    return pending.length > 0;
  };

  const loadAdminData = async (requestedPage?: number) => {
    const requestId = ++adminRequestRef.current;
    setLoading(true);
    setError('');
    try {
      const session = (await supabase?.auth.getSession())?.data.session;
      if (!session) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
      const query = new URLSearchParams({
        page: String(requestedPage ?? data?.pagination.page ?? 1),
        pageSize: String(pageSize),
        search: searchTerm.trim(),
        plan: planFilter,
        status: statusFilter,
      });
      const response = await monitoredFetch(`/api/admin/summary?${query}`, { headers: { authorization: `Bearer ${session.access_token}` } }, 'load admin summary');
      const body = await readApiJson<AdminData & { error?: string }>(response);
      if (!response.ok) {
        if (body.error === 'mfa_required') throw new Error('เซสชันนี้ยังไม่ได้ยืนยัน MFA');
        if (body.error === 'server_not_configured') throw new Error('Admin API ยังไม่ได้ตั้งค่า Secret Key บนเซิร์ฟเวอร์');
        throw new Error('ไม่มีสิทธิ์เข้าถึงหน้าจัดการระบบ');
      }
      if (adminRequestRef.current === requestId) {
        setData(body);
        setSelectedUserId('');
      }
    } catch (reason) {
      if (adminRequestRef.current === requestId) setError(reason instanceof Error ? reason.message : 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      if (adminRequestRef.current === requestId) setLoading(false);
    }
  };

  useEffect(() => {
    if (!data || adminSection!=='accounts' || !adminAuthorized) return;
    const timer = window.setTimeout(() => void loadAdminData(1), 350);
    return () => window.clearTimeout(timer);
  }, [searchTerm, planFilter, statusFilter, pageSize]);

  useEffect(()=>{if(adminAuthorized&&adminSection==='accounts')void loadAdminData();},[adminAuthorized,adminSection]);

  useEffect(() => {
    const checkMfa = async () => {
      if (!supabase) return;
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === 'aal2') {
        setAdminAuthorized(true);
        setMfaChecked(true);
        setLoading(false);
        return;
      }
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verified = factors?.totp.find((factor) => factor.status === 'verified');
      if (verified) {
        setFactorId(verified.id);
      } else {
        try {
          const cleared = await clearPendingAdminFactors();
          if (cleared) setMfaNotice('ล้างการตั้งค่า MFA ที่ค้างไว้แล้ว กรุณาเริ่มตั้งค่าใหม่อีกครั้ง');
        } catch {
          setError('ไม่สามารถล้างการตั้งค่า MFA ที่ค้างไว้ได้ กรุณาออกจากระบบแล้วลองใหม่');
        }
      }
      setMfaChecked(true);
      setLoading(false);
    };
    void checkMfa().catch(()=>{setAdminAuthorized(false);setMfaChecked(true);setLoading(false);setError('ตรวจสอบเซสชัน MFA ไม่สำเร็จ กรุณาเข้าสู่ระบบอีกครั้ง');});
  }, []);

  const beginMfaEnrollment = async () => {
    if (!supabase) return;
    setLoading(true);
    setError('');
    setMfaNotice('');
    try {
      await clearPendingAdminFactors();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'ไม่สามารถเริ่มตั้งค่า MFA ใหม่ได้');
      setLoading(false);
      return;
    }
    const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'BilaFlow Admin' });
    if (enrollError) setError(enrollError.message);
    else {
      setFactorId(enrolled.id);
      setEnrollment({ qr: enrolled.totp.qr_code, secret: enrolled.totp.secret });
    }
    setLoading(false);
  };

  const verifyMfa = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase || !factorId) return;
    setLoading(true);
    setError('');
    const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: mfaCode });
    if (verifyError) {
      setError('รหัสไม่ถูกต้องหรือหมดอายุ กรุณาลองอีกครั้ง');
      setLoading(false);
      return;
    }
    setEnrollment(null);
    setMfaCode('');
    setAdminAuthorized(true);
    setLoading(false);
  };

  const monitorMfaRequired=useMemo(()=>()=>{setAdminAuthorized(false);setData(null);setLoading(false);setError('กรุณายืนยัน MFA อีกครั้ง');void supabase?.auth.mfa.listFactors().then(({data:factors})=>setFactorId(factors?.totp.find(f=>f.status==='verified')?.id||''));},[]);

  const updateMember = async (userId: string, patch: { plan?: Plan; accountStatus?: 'active' | 'suspended'; planExpiresAt?: string | null }) => {
    setSavingUserId(userId);
    setError('');
    try {
      const session = (await supabase?.auth.getSession())?.data.session;
      if (!session) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
      const response = await monitoredFetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      }, 'update admin member');
      const body = await readApiJson<{ error?: string }>(response);
      if (!response.ok) {
        if (body.error === 'cannot_modify_self') throw new Error('เพื่อความปลอดภัย บัญชี Admin ไม่สามารถแก้สิทธิ์ของตัวเองจากหน้านี้');
        if (body.error === 'mfa_required') { monitorMfaRequired(); throw new Error('กรุณายืนยัน MFA ใหม่ก่อนแก้ไขบัญชี'); }
        if (body.error === 'cannot_modify_privileged_account') throw new Error('หน้านี้แก้ไขได้เฉพาะบัญชีลูกค้า');
        if (body.error === 'admin_audit_unavailable') throw new Error('บันทึกประวัติไม่ได้ จึงยังไม่แก้ไขบัญชี กรุณาลองใหม่ภายหลัง');
        if (body.error === 'admin_update_outcome_unknown') { await loadAdminData(data?.pagination.page ?? 1); throw new Error('ยังยืนยันผลการแก้ไขไม่ได้ โปรดตรวจสถานะล่าสุดก่อนทำรายการซ้ำ'); }
        if (body.error === 'server_not_configured') throw new Error('Admin API ยังไม่ได้ตั้งค่า Secret Key บนเซิร์ฟเวอร์');
        throw new Error('บันทึกข้อมูลสมาชิกไม่สำเร็จ');
      }
      await loadAdminData(data?.pagination.page ?? 1);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'บันทึกข้อมูลสมาชิกไม่สำเร็จ');
    } finally {
      setSavingUserId('');
    }
  };

  return <>
    <div className="page-heading"><div><span className="eyebrow">Privacy-first administration</span><h1>จัดการระบบโดยไม่เปิดเอกสารลูกค้า</h1><p>หน้านี้แสดงเฉพาะข้อมูลที่จำเป็นต่อบัญชีและแพ็กเกจ เนื้อหาเอกสารและไฟล์ไม่ถูกดึงมาแสดง</p></div><div className="privacy-pill"><ShieldCheck size={17} /><span>MFA protected</span><small>บังคับ AAL2 ที่ API</small></div></div>
    {!mfaChecked || (!adminAuthorized && loading && !factorId) ? <section className="content-card admin-message"><RefreshCw className="spin" size={20} /> กำลังตรวจสอบสิทธิ์…</section> : !adminAuthorized ? <section className="content-card mfa-card"><div className="mfa-lock"><ShieldCheck size={25} /></div><div className="mfa-copy"><span className="eyebrow">Admin security</span><h2>ยืนยันตัวตนสองขั้นตอน</h2><p>รหัสผ่านเพียงอย่างเดียวไม่เพียงพอสำหรับหน้า Admin ต้องยืนยันรหัสจากแอป Authenticator ในทุกเซสชัน</p>{mfaNotice && <div className="mfa-notice">{mfaNotice}</div>}{error && <div className="mfa-error">{error}</div>}{!factorId ? <button className="btn primary" onClick={() => void beginMfaEnrollment()} disabled={loading}>ตั้งค่า MFA สำหรับ Admin</button> : <form onSubmit={verifyMfa} className="mfa-form">{enrollment && <div className="mfa-enrollment"><img src={enrollment.qr} alt="QR code สำหรับตั้งค่า MFA" /><div><strong>สแกน QR ด้วยแอป Authenticator</strong><span>หรือกรอกรหัสลับด้วยตนเอง</span><code>{enrollment.secret}</code></div></div>}<label><span>รหัสยืนยัน 6 หลัก</span><input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} required /></label><button className="btn primary" disabled={loading || mfaCode.length !== 6}>{loading ? 'กำลังตรวจสอบ…' : 'ยืนยันและเปิดหน้า Admin'}</button></form>}</div></section> : <>
      <nav className="admin-section-tabs" aria-label="ส่วนจัดการระบบ"><button className="btn secondary" aria-pressed={adminSection==='system'} onClick={()=>setAdminSection('system')}>สถานะระบบ</button><button className="btn secondary" aria-pressed={adminSection==='accounts'} onClick={()=>setAdminSection('accounts')}>บัญชีผู้ใช้</button></nav>
      {adminSection==='system'?<Suspense fallback={<section className="content-card admin-message">กำลังเปิดสถานะระบบ…</section>}><SystemStatusDashboard key={currentUserId} userId={currentUserId} onMfaRequired={monitorMfaRequired}/></Suspense>:!data?<section className="content-card admin-message">{loading?'กำลังอ่านบัญชีผู้ใช้…':error||'ยังไม่มีข้อมูลบัญชี'}{!loading&&<button className="btn secondary" onClick={()=>void loadAdminData()}>ลองใหม่</button>}</section>:<>
      <section className="metric-grid admin-metrics">
        <article className="metric-card green"><span>บัญชีทั้งหมด</span><strong>{data.summary.total.toLocaleString('th-TH')}</strong><small>รวมบัญชีทั้งหมดในระบบ</small></article>
        <article className="metric-card dark"><span>เอกสารที่รายงาน</span><strong>{data.summary.reportedDocuments.toLocaleString('th-TH')}</strong><small>{data.summary.sharingAccounts} บัญชีแชร์สถิติ · Cloud Sync {data.summary.cloudSync}</small></article>
        <article className="metric-card cream"><span>แพ็กเกจชำระเงิน</span><strong>{data.summary.paid}</strong><small>Pro และ Business</small></article>
        <article className="metric-card lilac"><span>ระงับการใช้งาน</span><strong>{data.summary.suspended}</strong><small>สถานะบัญชีเท่านั้น</small></article>
      </section>
      <section className="content-card admin-health-summary"><div className="card-toolbar"><div><h2>สถานะ 24 ชั่วโมงล่าสุด</h2><span>แสดงเฉพาะจำนวนเหตุการณ์ ไม่เก็บเนื้อหาเอกสารหรืออีเมลเต็ม</span></div><div className="admin-scope"><ShieldCheck size={15} /> Monitoring</div></div><div className="admin-health-numbers"><div><strong>{data.observability.errors24h}</strong><span>ข้อผิดพลาด</span></div><div><strong>{data.observability.fatal24h}</strong><span>ร้ายแรง</span></div><div><strong>{data.observability.bounces24h}</strong><span>อีเมลตีกลับ/ส่งไม่สำเร็จ</span></div><div><strong>{data.observability.complaints24h}</strong><span>ร้องเรียนสแปม</span></div></div></section>
      <section className="content-card admin-table-card">
        <div className="card-toolbar"><div><h2>บัญชีผู้ใช้</h2><span>พบ {data.pagination.filtered.toLocaleString('th-TH')} บัญชี · หน้า {data.pagination.page} จาก {data.pagination.totalPages}</span></div><div className="admin-scope"><ShieldCheck size={15} /> Least privilege</div></div>
        <div className="admin-filters">
          <label className="admin-search"><Search size={15} /><input aria-label="ค้นหาบัญชี" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="ค้นหาชื่อหรืออีเมล" /></label>
          <select aria-label="กรองตามแพ็กเกจ" value={planFilter} onChange={(event) => setPlanFilter(event.target.value as 'all' | Plan)}><option value="all">ทุกแพ็กเกจ</option><option value="free">Free</option><option value="pro">Pro</option><option value="business">Business</option></select>
          <select aria-label="กรองตามสถานะ" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'suspended')}><option value="all">ทุกสถานะ</option><option value="active">ใช้งานปกติ</option><option value="suspended">ระงับ</option></select>
          <select aria-label="จำนวนบัญชีต่อหน้า" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value) as 25 | 50)}><option value="25">25 ต่อหน้า</option><option value="50">50 ต่อหน้า</option></select>
          <button type="button" className="btn secondary admin-refresh" onClick={() => void loadAdminData(data.pagination.page)} disabled={loading}><RefreshCw size={14} className={loading ? 'spin' : ''} /> รีเฟรช</button>
        </div>
        {error && <div className="admin-inline-error">{error}</div>}
        <div className="admin-table"><div className="admin-table-head"><span>บัญชี</span><span>แพ็กเกจ</span><span>วันหมดอายุ</span><span>เอกสาร</span><span>สถานะ</span><span>เข้าใช้ล่าสุด</span><span></span></div>{data.users.map((row) => {
          const isSelf = row.id === currentUserId;
          const saving = savingUserId === row.id;
          return <div className="admin-row" key={row.id}>
            <div className="admin-account"><strong>{row.displayName || 'ยังไม่ระบุชื่อ'}</strong><span>{row.email || `…${row.id.slice(-8)}`}</span>{isSelf && <small>บัญชีของคุณ</small>}</div>
            <select aria-label={`แพ็กเกจของ ${row.email}`} value={row.plan} disabled={isSelf || saving} onChange={(event) => void updateMember(row.id, { plan: event.target.value as Plan })}><option value="free">FREE</option><option value="pro">PRO</option><option value="business">BUSINESS</option></select>
            <input aria-label={`วันหมดอายุแพ็กเกจของ ${row.email}`} type="date" defaultValue={row.planExpiresAt?.slice(0, 10) ?? ''} disabled={isSelf || saving || row.plan === 'free'} onBlur={(event) => void updateMember(row.id, { planExpiresAt: event.target.value ? new Date(`${event.target.value}T23:59:59+07:00`).toISOString() : null })} />
            <span className="admin-document-count">{!row.usage ? 'ยังไม่รายงาน' : !row.usage.sharingEnabled ? 'ไม่แชร์สถิติ' : <><strong>{row.usage.quotes + row.usage.invoices + row.usage.receipts}</strong><small>QT {row.usage.quotes} · INV {row.usage.invoices} · RE {row.usage.receipts}</small></>}</span>
            <button type="button" className={`account-status ${row.status}`} disabled={isSelf || saving} onClick={() => void updateMember(row.id, { accountStatus: row.status === 'active' ? 'suspended' : 'active' })}>{saving ? 'กำลังบันทึก…' : row.status === 'active' ? 'ปกติ' : 'ระงับ'}</button>
            <span>{row.lastSignInAt ? shortDate(row.lastSignInAt) : 'ยังไม่เคย'}</span>
            <button type="button" className="admin-detail-button" onClick={() => setSelectedUserId(row.id)}>ดูรายละเอียด</button>
          </div>;
        })}{data.users.length === 0 && <div className="admin-empty">ไม่พบบัญชีที่ตรงกับตัวกรอง</div>}</div>
        <div className="admin-pagination"><span>แสดง {data.pagination.filtered === 0 ? 0 : ((data.pagination.page - 1) * data.pagination.pageSize) + 1}–{Math.min(data.pagination.page * data.pagination.pageSize, data.pagination.filtered)} จาก {data.pagination.filtered.toLocaleString('th-TH')}</span><div><button type="button" className="btn ghost small" disabled={loading || data.pagination.page <= 1} onClick={() => void loadAdminData(data.pagination.page - 1)}>ก่อนหน้า</button><strong>{data.pagination.page} / {data.pagination.totalPages}</strong><button type="button" className="btn ghost small" disabled={loading || data.pagination.page >= data.pagination.totalPages} onClick={() => void loadAdminData(data.pagination.page + 1)}>ถัดไป</button></div></div>
      </section>
      <section className="content-card admin-audit-card">
        <div className="card-toolbar"><div><h2>ประวัติการจัดการ</h2><span>50 รายการล่าสุด เพื่อให้ตรวจสอบย้อนหลังได้</span></div><FileClock size={19} /></div>
        <div className="admin-audit-list">{data.audit.length ? data.audit.map((entry) => <article key={entry.id} className="admin-audit-row"><div className="audit-mark"><ShieldCheck size={14} /></div><div><strong>{auditDescription(entry.details)}</strong><span>{entry.adminEmail || 'Admin'} → {entry.targetEmail || 'บัญชีที่ถูกจัดการ'}</span></div><time dateTime={entry.createdAt}>{dateTime(entry.createdAt)}</time></article>) : <div className="admin-empty">ยังไม่มีประวัติการจัดการ</div>}</div>
      </section>
      <div className="admin-privacy-note"><ShieldCheck size={19} /><div><strong>ขอบเขตข้อมูลของ Admin</strong><p>Admin API ไม่มี endpoint สำหรับอ่าน payload ของเอกสารหรือดาวน์โหลดไฟล์ลูกค้า การเพิ่มการดำเนินการสำคัญภายหลังควรใช้ step-up authentication และบันทึก audit log ทุกครั้ง</p></div></div>
      {selectedUser && <div className="admin-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedUserId(''); }}><aside className="admin-detail-panel" role="dialog" aria-modal="true" aria-label="รายละเอียดบัญชี"><header><div><span className="eyebrow">Account details</span><h2>{selectedUser.displayName || 'ยังไม่ระบุชื่อ'}</h2><p>{selectedUser.email || `…${selectedUser.id.slice(-8)}`}</p></div><button type="button" className="icon-btn" aria-label="ปิดรายละเอียด" onClick={() => setSelectedUserId('')}><X size={18} /></button></header><div className="admin-detail-grid"><div><span>แพ็กเกจ</span><strong>{selectedUser.plan.toUpperCase()}</strong></div><div><span>สถานะ</span><strong>{selectedUser.status === 'active' ? 'ใช้งานปกติ' : 'ระงับ'}</strong></div><div><span>บทบาทระบบ</span><strong>{selectedUser.role}</strong></div><div><span>Cloud Sync</span><strong>{selectedUser.cloudSync ? 'เปิด' : 'ปิด'}</strong></div><div><span>สมัครเมื่อ</span><strong>{dateTime(selectedUser.createdAt)}</strong></div><div><span>เข้าใช้ล่าสุด</span><strong>{selectedUser.lastSignInAt ? dateTime(selectedUser.lastSignInAt) : 'ยังไม่เคย'}</strong></div><div><span>ช่องทางสมัคร</span><strong>{selectedUser.authProvider === 'google' ? 'Google' : 'อีเมลและรหัสผ่าน'}</strong></div><div><span>ยืนยันอีเมล</span><strong>{selectedUser.emailConfirmedAt ? 'ยืนยันแล้ว' : 'ยังไม่ยืนยัน'}</strong></div><div><span>วันหมดอายุแพ็กเกจ</span><strong>{selectedUser.planExpiresAt ? dateTime(selectedUser.planExpiresAt) : 'ไม่มี'}</strong></div><div className="admin-id"><span>User ID</span><code>{selectedUser.id}</code></div></div><section className="admin-usage-detail"><div><span className="eyebrow">Usage metrics</span><h3>จำนวนเอกสารที่รายงาน</h3></div>{!selectedUser.usage ? <p>บัญชียังไม่เคยรายงานสถิติจากอุปกรณ์</p> : !selectedUser.usage.sharingEnabled ? <p>ผู้ใช้ปิดการแชร์สถิติการใช้งาน</p> : <><div className="admin-usage-numbers"><div><strong>{selectedUser.usage.quotes}</strong><span>ใบเสนอราคา</span></div><div><strong>{selectedUser.usage.invoices}</strong><span>ใบแจ้งหนี้</span></div><div><strong>{selectedUser.usage.receipts}</strong><span>ใบเสร็จ</span></div></div><p>30 วันล่าสุด {selectedUser.usage.recent30} รายการ · กิจกรรมล่าสุด {selectedUser.usage.lastActivityAt ? dateTime(selectedUser.usage.lastActivityAt) : 'ยังไม่มี'}<br />รายงานเมื่อ {selectedUser.usage.reportedAt ? dateTime(selectedUser.usage.reportedAt) : '—'} · แอป {selectedUser.usage.appVersion || '—'}</p></>}</section><div className="admin-detail-boundary"><ShieldCheck size={16} /><p>ตัวเลขนี้มาจากยอดรวมในอุปกรณ์ ไม่มีเลขที่เอกสาร ชื่อลูกค้า ยอดเงิน สลิป หรือไฟล์ของผู้ใช้</p></div></aside></div>}
      </>}
    </>}
  </>;
}

function PlansView({ userId, currentPlan, planExpiresAt, onSubscriptionChange }: { userId: string; currentPlan: Plan; planExpiresAt: string | null; onSubscriptionChange: (message: string) => void }) {
  const [trialConsent, setTrialConsent] = useState(false);
  const [trialEligible, setTrialEligible] = useState(false);
  const [renewalMode, setRenewalMode] = useState<'manual' | 'automatic'>('manual');
  const [autoRenewConsent, setAutoRenewConsent] = useState(false);
  const [checkingOut, setCheckingOut] = useState<'promptpay' | 'card' | 'automatic' | 'trial' | null>(null);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [subscriptionAction, setSubscriptionAction] = useState<'cancel' | 'resume' | null>(null);
  const [subscription, setSubscription] = useState<{
    plan: Plan;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    isIntroTrial?: boolean;
    trialEndsAt?: string | null;
  } | null>(null);
  const plans = [
    { name: 'Free', price: '฿0', className: 'free', icon: FileText, features: ['ใบเสนอราคา ใบแจ้งหนี้ และใบเสร็จ PDF', 'ช่องลงนามและบันทึกหลักฐานการยอมรับ', 'ล็อกเอกสารและสร้างฉบับแก้ไขอย่างปลอดภัย', 'บันทึกรับเงินหลายงวดและคำนวณยอดคงเหลือ', 'แนบสลิปในเครื่องและสำรองข้อมูล', 'มีเครดิต BilaFlow ท้ายเอกสาร'] },
    { name: 'Pro', price: '฿249', className: 'pro', icon: Sparkles, featured: true, features: ['ทุกอย่างใน Free', 'Cloud Sync 5 GB ต่อบัญชี · หลายอุปกรณ์', 'สำรองข้อมูลและสลิปอัตโนมัติ', 'รายงานลูกหนี้และแจ้งเตือนครบกำหนด'] },
    { name: 'Business', price: '฿899', className: 'business', icon: Users, features: ['ทุกอย่างใน Pro', 'พื้นที่ทำงานสำหรับทีม', 'กำหนดบทบาทและสิทธิ์', 'แบรนด์และเทมเพลตเพิ่มเติม'] },
  ];

  useEffect(() => {
    let active = true;
    const loadSubscription = async () => {
      if (!supabase) return;
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;
      const eligibilityResponse = await monitoredFetch('/api/billing/trial', {
        headers: { authorization: `Bearer ${session.access_token}` },
      }, 'load Pro trial eligibility');
      if (eligibilityResponse.ok) {
        const eligibility = await readApiJson<{ eligible?: boolean }>(eligibilityResponse);
        if (active) setTrialEligible(eligibility.eligible === true);
      }
      const response = await monitoredFetch('/api/billing/subscription', {
        headers: { authorization: `Bearer ${session.access_token}` },
      }, 'load billing subscription');
      if (!response.ok) return;
      const result = await readApiJson<{ subscription?: typeof subscription }>(response);
      if (active) {
        setSubscription(result.subscription ?? null);
        if (result.subscription && ['active', 'trialing', 'past_due', 'unpaid'].includes(result.subscription.status)) setRenewalMode('automatic');
      }
    };
    void loadSubscription().catch(() => { /* Purchases remain available if status cannot be loaded. */ });
    return () => { active = false; };
  }, []);

  const beginTrial = async () => {
    if (!supabase || checkingOut || checkoutPending || !trialConsent) return;
    setCheckoutError('');
    setCheckingOut('trial');
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
      const response = await monitoredFetch('/api/billing/trial', {
        method: 'POST', headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ consent: true, consentVersion: '2026-10-24' }),
      }, 'start Pro trial');
      const result = await readApiJson<{ url?: string; error?: string }>(response);
      if (!response.ok || !result.url) {
        if (result.error === 'billing_checkout_in_progress' || result.error === 'billing_checkout_conflict') { window.dispatchEvent(new Event('bilaflow:checkout-pending-updated')); throw new Error('มีรายการชำระค้างอยู่ กรุณาตรวจรายการเดิมด้านบนก่อน'); }
        if (response.status === 409) { setTrialEligible(false); throw new Error('บัญชีนี้ไม่สามารถทดลองซ้ำได้ แต่ยังสมัคร Pro แบบจ่ายเงินได้ตามปกติ'); }
        throw new Error('เปิดหน้าทดลองไม่สำเร็จ ยังไม่มีการเรียกเก็บเงิน กรุณาลองอีกครั้ง');
      }
      window.location.assign(result.url);
    } catch (reason) {
      setCheckoutError(reason instanceof Error ? reason.message : 'เปิดหน้าทดลองไม่สำเร็จ');
      setCheckingOut(null);
    }
  };

  const beginCheckout = async (paymentMethod: 'promptpay' | 'card') => {
    if (!supabase || checkingOut || checkoutPending) return;
    if (renewalMode === 'automatic' && !autoRenewConsent) {
      setCheckoutError('กรุณายืนยันว่าคุณอนุญาตให้ตัดบัตรอัตโนมัติทุก 31 วัน');
      return;
    }
    setCheckoutError('');
    setCheckingOut(renewalMode === 'automatic' ? 'automatic' : paymentMethod);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
      const response = await monitoredFetch('/api/billing/checkout', {
        method: 'POST',
        headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ plan: 'pro', period: 'monthly', paymentMethod, renewalMode }),
      }, 'start Pro checkout');
      const result = await readApiJson<{ url?: string; error?: string }>(response);
      if (!response.ok || !result.url) {
        if (result.error === 'payment_setup_required') throw new Error('ระบบชำระเงินยังตั้งค่าไม่ครบ กรุณาติดต่อผู้ดูแล');
        if (result.error === 'subscription_setup_required') throw new Error('ระบบต่ออัตโนมัติยังติดตั้งฐานข้อมูลไม่ครบ กรุณาติดต่อผู้ดูแล');
        if (result.error === 'subscription_exists') throw new Error('บัญชีนี้เปิดการต่ออัตโนมัติอยู่แล้ว');
        if (result.error === 'automatic_subscription_active') throw new Error('กรุณายกเลิกการต่ออัตโนมัติและรอให้สิ้นสุดรอบก่อนซื้อแบบต่อเอง');
        if (result.error === 'billing_checkout_in_progress' || result.error === 'billing_checkout_conflict') { window.dispatchEvent(new Event('bilaflow:checkout-pending-updated')); throw new Error('มีรายการชำระค้างอยู่ กรุณาตรวจรายการเดิมด้านบนก่อน'); }
        throw new Error('เปิดหน้าชำระเงินไม่สำเร็จ กรุณาลองอีกครั้ง');
      }
      window.location.assign(result.url);
    } catch (reason) {
      setCheckoutError(reason instanceof Error ? reason.message : 'เปิดหน้าชำระเงินไม่สำเร็จ');
      setCheckingOut(null);
    }
  };

  const updateSubscription = async (action: 'cancel' | 'resume') => {
    if (!supabase || subscriptionAction) return;
    setCheckoutError('');
    setSubscriptionAction(action);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) throw new Error('กรุณาเข้าสู่ระบบอีกครั้ง');
      const response = await monitoredFetch('/api/billing/subscription', {
        method: 'POST',
        headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ action }),
      }, `${action} billing subscription`);
      const result = await readApiJson<{ subscription?: typeof subscription; error?: string }>(response);
      if (!response.ok || !result.subscription) throw new Error('ปรับการต่ออัตโนมัติไม่สำเร็จ กรุณาลองอีกครั้ง');
      setSubscription(result.subscription);
      onSubscriptionChange(action === 'cancel'
        ? 'ยกเลิกการต่ออัตโนมัติแล้ว ยังใช้สิทธิ์ได้ถึงวันหมดอายุ และจะไม่ตัดเงินรอบถัดไป'
        : 'เปิดการต่ออัตโนมัติอีกครั้งแล้ว ระบบจะตัดบัตรตามกำหนดที่แสดง');
    } catch (reason) {
      setCheckoutError(reason instanceof Error ? reason.message : 'ปรับการต่ออัตโนมัติไม่สำเร็จ');
    } finally {
      setSubscriptionAction(null);
    }
  };

  const subscriptionLive = Boolean(subscription && ['active', 'trialing', 'past_due', 'unpaid'].includes(subscription.status));

  return <>
    <div className="page-heading centered"><div><span className="eyebrow">แพ็กเกจที่เติบโตไปกับคุณ</span><h1>เริ่มใช้ฟรี อัปเกรดเมื่อจำเป็น</h1><p>ทุกแพ็กเกจให้คุณเป็นเจ้าของข้อมูล และดาวน์โหลดสำรองได้เสมอ</p></div></div>
    {userId && <BillingCheckoutRecovery key={`checkout-${userId}`} userId={userId} onPendingChange={setCheckoutPending} />}
    <div className="plan-grid">{plans.map((item) => {
      const Icon = item.icon;
      const active = currentPlan === item.name.toLowerCase();
      const isPro = item.name === 'Pro';
      return <article className={`plan-card ${item.className} ${item.featured ? 'featured' : ''}`} key={item.name}>
        {item.featured && <span className="popular">แนะนำ</span>}
        <div className="plan-icon"><Icon size={22} /></div>
        <h2>{item.name}</h2>
        <div className="plan-price"><strong>{item.price}</strong><span>{item.price !== '฿0' ? '/ 31 วัน' : 'ตลอดไป'}</span></div>
        {isPro && active && planExpiresAt && <div className="current-entitlement"><Check size={15} /> ใช้งาน Pro ถึง {shortDate(planExpiresAt)}</div>}
        <ul>{item.features.map((feature) => <li key={feature}><Check size={16} />{feature}</li>)}</ul>
        {isPro ? <div className="checkout-actions">
          {trialEligible && !subscriptionLive && <section className="pro-trial-offer" aria-label="ทดลอง Pro ฟรี">
            <strong>ทดลอง Pro ฟรี 7 วัน</strong>
            <p>ใช้ Pro ครบทุกฟีเจอร์ พร้อม Cloud 5 GB · ต้องผูกบัตร · บัญชีและบัตรรับสิทธิ์ได้ครั้งเดียว</p>
            <label className="auto-renew-consent"><input type="checkbox" checked={trialConsent} onChange={(event) => setTrialConsent(event.target.checked)} /><span>ฉันยินยอมให้ตัดบัตร <strong>฿249 เมื่อครบ 7 วัน และทุก 31 วันถัดไป</strong> หากไม่ยกเลิกก่อนหมดทดลอง ยกเลิกได้ที่หน้านี้ <a href="/terms.html" target="_blank" rel="noreferrer">ดูเงื่อนไข</a></span></label>
            <button className="btn primary" type="button" disabled={Boolean(checkingOut) || checkoutPending || !trialConsent} onClick={() => void beginTrial()}>{checkingOut === 'trial' ? 'กำลังเปิด…' : 'เริ่มทดลองฟรี 7 วันด้วยบัตร'}</button>
            <small>ตรวจสิทธิ์ก่อนเปิด Trial · ถ้าไม่ได้สิทธิ์จะไม่ตัดเงินและไม่สมัครแบบจ่ายเงินให้อัตโนมัติ</small>
          </section>}
          {!subscriptionLive && <strong className="paid-pro-heading">สมัคร Pro แบบจ่ายเงินทันที</strong>}
          <div className="renewal-selector" role="group" aria-label="รูปแบบการต่อสิทธิ์">
            <button type="button" className={renewalMode === 'manual' ? 'active' : ''} aria-pressed={renewalMode === 'manual'} onClick={() => { setRenewalMode('manual'); setCheckoutError(''); }}>ต่อเอง</button>
            <button type="button" className={renewalMode === 'automatic' ? 'active' : ''} aria-pressed={renewalMode === 'automatic'} onClick={() => { setRenewalMode('automatic'); setCheckoutError(''); }}>ต่ออัตโนมัติ</button>
          </div>
          {renewalMode === 'manual' ? <>
            <button className="btn primary promptpay-button" type="button" disabled={Boolean(checkingOut) || checkoutPending} onClick={() => void beginCheckout('promptpay')}><QrCode size={17} />{checkingOut === 'promptpay' ? 'กำลังเปิด…' : 'ชำระด้วย PromptPay'}<small>ค่าธรรมเนียมต่ำกว่า · แนะนำ</small></button>
            <button className="btn ghost" type="button" disabled={Boolean(checkingOut) || checkoutPending} onClick={() => void beginCheckout('card')}><CreditCard size={17} />{checkingOut === 'card' ? 'กำลังเปิด…' : 'ชำระด้วยบัตร'}</button>
            <small className="renewal-note">จ่ายครั้งเดียว ได้สิทธิ์ 31 วัน แล้วค่อยต่อเอง</small>
          </> : subscriptionLive ? <div className={`subscription-summary ${subscription?.cancelAtPeriodEnd ? 'ending' : ''}`}>
            <strong>{subscription?.cancelAtPeriodEnd ? 'ตั้งค่าให้หยุดต่ออัตโนมัติแล้ว' : subscription?.isIntroTrial ? 'กำลังทดลอง Pro ฟรี 7 วัน' : 'เปิดต่ออัตโนมัติอยู่'}</strong>
            <span>{subscription?.currentPeriodEnd ? `${subscription.cancelAtPeriodEnd ? 'ใช้สิทธิ์ได้ถึง' : subscription.isIntroTrial ? 'ตัดบัตรครั้งแรก ฿249 วันที่' : 'รอบถัดไป'} ${new Date(subscription.currentPeriodEnd).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}` : 'ตัดบัตรทุก 31 วัน'}</span>
            {subscription?.isIntroTrial && <small>{subscription.cancelAtPeriodEnd ? 'จะไม่ตัดเงินเมื่อหมดทดลอง' : 'หลังจากนั้นต่อ ฿249 ทุก 31 วัน · ยกเลิกก่อนเวลาข้างต้นเพื่อไม่ให้ตัดเงิน'}</small>}
            <button className="btn ghost" type="button" disabled={Boolean(subscriptionAction)} onClick={() => void updateSubscription(subscription?.cancelAtPeriodEnd ? 'resume' : 'cancel')}>
              {subscriptionAction ? 'กำลังบันทึก…' : subscription?.cancelAtPeriodEnd ? 'เปิดต่ออัตโนมัติอีกครั้ง' : subscription?.isIntroTrial ? 'ยกเลิกการต่ออัตโนมัติหลังทดลอง' : 'ยกเลิกเมื่อสิ้นสุดรอบ'}
            </button>
          </div> : <div className="automatic-checkout">
            <label className="auto-renew-consent"><input type="checkbox" checked={autoRenewConsent} onChange={(event) => setAutoRenewConsent(event.target.checked)} /><span>ยินยอมให้ Stripe ตัดบัตร <strong>฿249 ทุก 31 วัน</strong> จนกว่าจะยกเลิก</span></label>
            <button className="btn primary" type="button" disabled={Boolean(checkingOut) || checkoutPending || !autoRenewConsent} onClick={() => void beginCheckout('card')}><CreditCard size={17} />{checkingOut === 'automatic' ? 'กำลังเปิด…' : 'เปิดต่ออัตโนมัติด้วยบัตร'}</button>
            <small className="renewal-note">{active && planExpiresAt ? `เริ่มตัดครั้งแรกหลังสิทธิ์เดิมหมด ${shortDate(planExpiresAt)}` : 'ตัดครั้งแรกวันนี้ แล้วต่อทุก 31 วัน'} · ยกเลิกได้ทุกเมื่อ</small>
          </div>}
          {checkoutError && <p className="checkout-error" role="alert">{checkoutError}</p>}
        </div> : item.name === 'Business' ? <div className="business-coming-soon"><div><span>ต่อเอง</span><span>ต่ออัตโนมัติ</span></div><button className="btn ghost" disabled>เร็ว ๆ นี้</button></div> : <button className={`btn ${item.featured ? 'primary' : 'ghost'}`} disabled={active}>{active ? 'แพ็กเกจปัจจุบัน' : 'เริ่มใช้งาน'}</button>}
      </article>;
    })}</div>
    {renewalMode === 'manual' && userId && <ManualStoragePanel key={userId} userId={userId} />}
    <div className="plan-note"><ShieldCheck size={19} /><span>ชำระผ่าน Stripe · BilaFlow ไม่เก็บข้อมูลบัตร · ทั้งต่อเองและอัตโนมัติได้สิทธิ์เท่ากันรอบละ 31 วัน</span></div>
  </>;
}

export default App;
